/**
 * Shared Excel executor helpers.
 *
 * Shared helpers for the native ReAct harness.
 */
import {
  TABULAR_TOPIC_TERM,
  TABULAR_TOPIC_TERM_GLOBAL,
} from "./tabularTopicTerms";
import {
  carriesClassifiableSignal,
  classifyWorksheetColumnIntent,
  matchWorksheetColumnAlias,
  type WorksheetColumnIntent,
} from "./worksheetColumnIntent";
import {
  detectIdLookupRoutingIntent,
  type IdLookupRoutingIntent,
} from "./idLookupRoutingPolicy";
import {
  type ToolCallContext,
  type ToolCallDetailedResult,
} from "./toolRegistry";
import {
  type ExecutorRunAccumulators,
  type ExecutorRunSessionState,
  type ExecutorWorkbookHandoff,
  type RuntimeToolExecutor,
} from "./executorToolRuntime";
import {
  recordColumnSizingIntent,
} from "./columnSizingPolicy";
import {
  isFdsBoundFormula,
} from "./formulaWriteDetection";
import type {
  ApplyTemplateOutcome,
} from "./templateTools";
import type { ExecutorBlockedOperation } from "./executorRunTypes";
import {
  extractSourcesFromCitations,
  type ExcelAgentSource,
} from "./agentTimeline";
import {
  diag,
} from "./aiDiag";
import {
  buildSkillContextBlock,
  withPushSkills,
} from "./excelSkills";
import {
  getExcelBrandDefaults,
} from "./branding/excelBrandDefaults";
import {
  columnIndex,
  numberValue,
  stringValue,
} from "./excelValueUtils";
import {
  classifyHardErrorsInReadRangeResult,
  classifyHardErrorsInFormulaValues,
  type ExcelHardErrorCell,
} from "./excelHardErrors";
import {
  extractCitedValuesById,
  ingestAgentResponse,
} from "./citationNotes";
import {
  evaluateWorksheetContentReadback,
  hasDenseWorksheetContentReadbackEvidence as hasDenseReadbackEvidence,
} from "./worksheetContentReadback";
import type {
  ExecutorContentReadbackTarget,
  ExecutorExpectedContentCell,
  ExecutorReadbackTarget,
  ExecutorRunEvent,
  ExecutorVerificationState,
  PendingShapeCorrection,
} from "./executorRunTypes";
export const STATEFUL_TOOLS = new Set<string>([
  "set_research_route",
  // FactSet GetRangeSnapshot uses Excel CopyPicture internally; serialize it
  // so parallel captures cannot clear or overwrite one another's clipboard work.
  "get_snapshot",
  "modify_workbook",
  "set_range_values",
  "set_range_formulas",
  "format_range",
  "clear_range",
  "edit_range_structure",
  "layout_range",
  "sort_range",
  "manage_worksheet",
  "copy_worksheet",
  "manage_named_range",
  "insert_company_logo",
  "set_comment",
  "delete_comment",
  "set_note",
  "delete_note",
  "set_citations",
  "create_sheet",
  "rename_sheet",
  "activate_sheet",
  "apply_template",
  "refresh_factset_data",
  "recalculate_workbook",
  "open_workbook",
  "create_workbook",
  "activate_workbook",
]);

export const WRITE_TOOLS = new Set<string>([
  "modify_workbook",
  "set_range_values",
  "set_range_formulas",
  "format_range",
  "clear_range",
  "edit_range_structure",
  "layout_range",
  "sort_range",
  "manage_worksheet",
  "copy_worksheet",
  "manage_named_range",
  "insert_company_logo",
  "set_comment",
  "delete_comment",
  "set_note",
  "delete_note",
  "set_citations",
  "create_sheet",
  "rename_sheet",
  "activate_sheet",
  "apply_template",
]);

/**
 * Writes protected by the executor's argument-only replay fingerprint.
 *
 * Worksheet workload commits own their lifecycle and exact read-back
 * verification. Their run-scoped IDs can be reused after resume, so a generic
 * fingerprint can otherwise suppress a new controller's first commit and leave
 * its active wave permanently open.
 *
 * Membership must stay all-or-nothing per tool: it gates the fingerprint, the
 * `blockedOperation` guards, and the assignment of `blockedOperation` itself.
 * Re-admitting commits to only the guards would deadlock them, because every
 * wave commits the same `{workloadId}` arguments -- so an exact-match guard
 * would treat wave N+1 as a replay of wave N, and the overlap guard fails
 * closed when it cannot derive a target range from those arguments. Commits are
 * instead safe to replay on their own terms: each one re-reads its targets and
 * writes only rows that come back blank.
 */
export const REPLAY_GUARDED_WRITE_TOOLS = new Set(WRITE_TOOLS);

/** Whether a confirmed write may be suppressed by its semantic fingerprint. */
export function isConfirmedWriteDeduplicationCandidate(
  toolName: string,
  rawInput?: unknown,
): boolean {
  if (!isReplayGuardedWriteToolCall(toolName, rawInput)) return false;
  if (
    toolName === "copy_worksheet" ||
    toolName === "edit_range_structure" ||
    toolName === "insert_company_logo" ||
    toolName === "manage_named_range"
  ) {
    return false;
  }
  if (toolName === "manage_worksheet") {
    return recordValue(rawInput).operation === "activate";
  }
  return true;
}

/** Cell annotation writes whose ambiguous acknowledgements are isolated. */
export const ANNOTATION_WRITE_TOOLS = new Set([
  "set_comment",
  "delete_comment",
  "set_note",
  "delete_note",
  "set_citations",
]);

export const CHART_TOOL_NAMES = new Set([
  "range_chart",
  "native_graph", // dispatcher-only compatibility alias
  "active_graph",
  "native_chart",
]);

/**
 * The range-chart tool and the pre-rename alias the dispatcher still accepts.
 * Guards keyed to only one of the two silently stop firing, so they all go
 * through here.
 */
export function isRangeChartToolName(name: string): boolean {
  return name === "range_chart" || name === "native_graph";
}

/**
 * Every chart operation that only reads, including the pre-rename ids a paused
 * run can still replay. Listing the reads rather than the writes is what makes
 * the classification below safe by default: a new or misspelled operation is
 * gated as a write instead of slipping past the workbook guards.
 */
export const READ_ONLY_CHART_OPERATIONS = new Set([
  "read_range_chart",
  "get_range_chart_image",
  "read_native_graph",
  "get_native_graph_image",
  "read_active_graph",
  "list_active_graph_stylesets",
  "read_native_chart",
]);

export function chartOperation(rawInput: unknown): string | null {
  if (!rawInput || typeof rawInput !== "object" || Array.isArray(rawInput)) {
    return null;
  }
  const operation = (rawInput as Record<string, unknown>).operation;
  return typeof operation === "string" ? operation : null;
}

export function isChartWriteCall(toolName: string, rawInput: unknown): boolean | null {
  if (!CHART_TOOL_NAMES.has(toolName)) return null;
  const operation = chartOperation(rawInput);
  return !operation || !READ_ONLY_CHART_OPERATIONS.has(operation);
}
/** Operation-aware shared-state classification for executor tool calls. */
export function isStatefulToolCall(
  toolName: string,
  rawInput?: unknown,
): boolean {
  return isChartWriteCall(toolName, rawInput) ?? STATEFUL_TOOLS.has(toolName);
}
/**
 * Classify a write for generic replay protection, excluding workload commits.
 *
 * @param toolName - Provider-visible executor tool name.
 * @param rawInput - Parsed arguments for operation-aware chart calls.
 * @returns Whether fingerprint and ambiguity guards apply.
 */
export function isReplayGuardedWriteToolCall(
  toolName: string,
  rawInput?: unknown,
): boolean {
  return (
    isChartWriteCall(toolName, rawInput) ??
    REPLAY_GUARDED_WRITE_TOOLS.has(toolName)
  );
}

export function requiresSourceConstrainedResearch(
  researchContext?: ToolCallContext["researchContext"]
): boolean {
  return (researchContext?.userFileIds.length ?? 0) > 0;
}

/**
 * Direct lookup must not bypass a source allowlist or uploaded-file request.
 * Those runs stay entirely on the selected research path.
 */
export function isDirectIdLookupSourceRestricted(
  researchContext?: ToolCallContext["researchContext"]
): boolean {
  return (
    requiresSourceConstrainedResearch(researchContext) ||
    researchContext?.selectionMode === "filtered" ||
    researchContext?.selectionMode === "user_files"
  );
}
/**
 * Model, template, and visualization builds can mention worksheet ranges and
 * tabular metrics without asking for one aligned research result. Keep those
 * tasks on the normal workbook-editing path.
 */
export const WORKBOOK_BUILD_REQUEST =
  /\b(?:dcf|lbo|three[-\s]statement|3[-\s]statement|operating\s+model|financial\s+model|merger\s+model|credit\s+model|debt\s+schedule|precedent\s+transactions?|football\s+field|sensitivity\s+(?:table|analysis)|scenario\s+analysis|data\s+table|waterfall|pivot\s+table|template|chart|graph|dashboard)\b/i;
export const COMPS_WORKBOOK_BUILD_REQUEST =
  /(?:\b(?:build|create|construct|generate|make|prepare|set\s*up)\b.{0,80}\b(?:trading\s+comps|comparable\s+compan(?:y|ies))\b|\b(?:trading\s+comps|comparable\s+compan(?:y|ies))\b.{0,80}\b(?:build|create|construct|generate|make|prepare|set\s*up)\b)/i;

/**
 * Identifies worksheet-scoped requests for a supported tabular topic.
 *
 * The model still supplies exact ranges after inspecting the workbook; this
 * detector only decides whether the gated run-scoped lifecycle should replace
 * the competing free-form research route.
 */
export function detectTabularQueryWorksheetWorkload(userRequest: string): boolean {
  const request = userRequest.trim();
  if (!request) return false;
  if (
    WORKBOOK_BUILD_REQUEST.test(request) ||
    COMPS_WORKBOOK_BUILD_REQUEST.test(request)
  ) {
    return false;
  }

  // Strip catalog topics before testing aggregate intent. Otherwise metric
  // names such as "Total Assets" and "Employee Count" look like requests to
  // summarize the list as a whole.
  const requestWithoutTopics = request.replace(
    TABULAR_TOPIC_TERM_GLOBAL,
    " ",
  );
  const hasExplicitPerRowTargets =
    /\b(?:each|every)\s+(?:\w+\s+){0,2}(?:compan(?:y|ies)|issuers?|entities|tickers?)\b/i.test(
      request
    );
  const hasSequencedPerRowAction =
    hasExplicitPerRowTargets &&
    /(?:\b(?:populate|fill|write|retrieve|research|find|look\s*up|answer|add)\b.{0,160}\b(?:then|after(?:ward)?s?)\b|\b(?:then|after(?:ward)?s?)\b.{0,160}\b(?:populate|fill|write|retrieve|research|find|look\s*up|answer|add)\b)/i.test(
      request
    );
  if (
    ASKS_ABOUT_THE_LIST_AS_A_WHOLE.test(requestWithoutTopics) &&
    !hasSequencedPerRowAction
  ) {
    return false;
  }
  // "These companies" or "for each company" points at the open list as plainly
  // as "column A" does, even when the list contains only one company.
  const referencesTheOpenList =
    /\b(?:these|those)\s+(?:\w+\s+){0,2}(?:compan(?:y|ies)|issuers?|entities|tickers?|names?)\b/i.test(
      request
    ) ||
    hasExplicitPerRowTargets ||
    /\ball\s+(?:\w+\s+){0,2}(?:compan(?:y|ies)|issuers?|entities|tickers?)\b/i.test(
      request
    );
  const hasWorksheetScope =
    referencesTheOpenList ||
    /\b(?:worksheet|sheet|column|range|cells?|rows?)\b/i.test(request) ||
    /\b[A-Z]{1,3}\d+\s*:\s*[A-Z]{1,3}\d+\b/i.test(request);
  const hasCatalogTopic =
    /\b(?:(?:latest|current|closing|close|end[-\s]+of[-\s]+day|eod)\s+)?(?:share|stock|equity)\s+prices?\b/i.test(
      request
    ) ||
    /\bprices?\s+per\s+share\b/i.test(request) ||
    TABULAR_TOPIC_TERM.test(request);
  const hasPerRowCompanyTargets =
    referencesTheOpenList &&
    /\b(?:compan(?:y|ies)|issuers?|entities|tickers?)\b/i.test(request);
  const hasTabularTopic = hasCatalogTopic || hasPerRowCompanyTargets;
  const hasRequestedOutput =
    /\b(?:populate|fill|write|return|retrieve|research|find|look\s*up|answer|add)\b/i.test(request) ||
    // A per-row question asked directly, such as "Who are the current CEOs of
    // these companies?", requests one answer per row without naming a verb.
    (referencesTheOpenList && /^(?:who|what|which|when|where)\b/i.test(request));
  return hasWorksheetScope && hasTabularTopic && hasRequestedOutput;
}

/**
 * Questions about the list as a whole: one answer summarizing, counting, or
 * filtering the companies, rather than one answer per row.
 */
export const ASKS_ABOUT_THE_LIST_AS_A_WHOLE =
  /\b(?:how\s+many|which\s+of|any\s+of|average|median|mean|total|sum|aggregate|count|compare|rank|sort|filter|top\s+\d+|bottom\s+\d+|summar(?:y|ize|ise))\b/i;

export const EXPLICIT_TABULAR_QUERY_WORKLOAD_REQUEST =
  /\b(?:aligned\s+)?tabular[-\s]+query(?:[-\s]+(?:workload|workflow|path|route))?\b/i;

/**
 * Gives an explicit tabular-query workflow request precedence over incidental
 * identity language such as an "Ultimate Parent Company" source-column label.
 */
export function shouldRouteToTabularQueryWorksheetWorkload(
  userRequest: string,
  enabled: boolean
): boolean {
  if (!enabled || !detectTabularQueryWorksheetWorkload(userRequest)) {
    return false;
  }
  if (EXPLICIT_TABULAR_QUERY_WORKLOAD_REQUEST.test(userRequest)) {
    return true;
  }
  const idLookupIntent = detectIdLookupRoutingIntent(userRequest);
  return !idLookupIntent.active;
}

export interface WorksheetColumnRoute {
  route: "tabular" | "id_lookup" | "none";
  /** Catalog topic the request resolved to, when one was identified. */
  topicKey?: string;
  topicName?: string;
  score?: number;
  confidence?: WorksheetColumnIntent["confidence"];
  usedEmbeddings?: boolean;
  /**
   * The request is a terse identity-column alias whose worksheet scope comes
   * from the active company list rather than from explicit request wording.
   */
  inferredWorksheetList?: boolean;
  /**
   * Intent to use downstream. Either the detected intent, or one synthesized
   * from a classifier-identified identity topic the phrase detector missed.
   */
  idLookupIntent: IdLookupRoutingIntent;
  reason: string;
}

/**
 * Chooses the workload that owns an aligned worksheet-column request.
 *
 * The old gate required the request text to carry its own worksheet scope and
 * a verb from a fixed list, so terse phrasings such as "Pull CEO names" fell
 * through to free-form research. Scope now comes from the workbook itself and
 * subject matter from the topic classifier, with the lexical detector kept for
 * the case where embeddings are unreachable.
 */
export async function resolveWorksheetColumnRoute(input: {
  userRequest: string;
  tabularEnabled: boolean;
  companyListPresent: boolean;
  idLookupIntent: IdLookupRoutingIntent;
  signal?: AbortSignal;
}): Promise<WorksheetColumnRoute> {
  const { userRequest, tabularEnabled, companyListPresent } = input;
  const detected = input.idLookupIntent;
  const inactive: IdLookupRoutingIntent = { active: false, requestedFields: [] };

  // An explicit request for the tabular workflow outranks incidental identity
  // language, such as an "Ultimate Parent Company" source-column label.
  if (
    tabularEnabled &&
    EXPLICIT_TABULAR_QUERY_WORKLOAD_REQUEST.test(userRequest) &&
    detectTabularQueryWorksheetWorkload(userRequest)
  ) {
    return {
      route: "tabular",
      idLookupIntent: { active: false, requestedFields: [] },
      reason: "explicit_tabular_request",
    };
  }

  // A deterministic user-request alias outranks identity language found only
  // in generated plan hints. This keeps requests such as "Pull CEOs" on the
  // tabular route even if a plan mentions an Ultimate Parent Company column.
  const aliasIntent = matchWorksheetColumnAlias(userRequest);
  const aliasDiagnostics = aliasIntent
    ? {
        score: aliasIntent.score,
        confidence: aliasIntent.confidence,
        usedEmbeddings: aliasIntent.usedEmbeddings,
      }
    : {};
  if (aliasIntent?.owner === "id_lookup" && aliasIntent.idLookupField) {
    // The phrase detector only claims a parent request when the word
    // "ultimate" appears, so "Fill parents" and "Parent co?" can arrive
    // inactive. Downstream eligibility reads `.active`, and the policy is
    // built from `requestedFields`, so synthesize the complete intent here.
    return {
      route: "id_lookup",
      topicKey: aliasIntent.topicKey,
      topicName: aliasIntent.topicName,
      ...aliasDiagnostics,
      inferredWorksheetList: companyListPresent,
      idLookupIntent: {
        active: true,
        requestedFields: [aliasIntent.idLookupField],
      },
      reason: "id_lookup_topic_classified",
    };
  }

  if (aliasIntent?.owner === "none") {
    return {
      route: "none",
      ...aliasDiagnostics,
      idLookupIntent: inactive,
      reason: "classified_not_per_row",
    };
  }

  if (aliasIntent?.owner === "tabular") {
    if (!tabularEnabled) {
      return {
        route: "none",
        ...aliasDiagnostics,
        idLookupIntent: inactive,
        reason: "tabular_disabled",
      };
    }
    if (!companyListPresent) {
      return {
        route: "none",
        ...aliasDiagnostics,
        idLookupIntent: inactive,
        reason: "no_company_list",
      };
    }
    return {
      route: "tabular",
      topicKey: aliasIntent.topicKey,
      topicName: aliasIntent.topicName,
      ...aliasDiagnostics,
      idLookupIntent: inactive,
      reason: "topic_classified",
    };
  }

  // The phrase detector remains authoritative when the request itself did not
  // resolve to a deterministic catalog alias.
  if (detected.active) {
    return {
      route: "id_lookup",
      idLookupIntent: detected,
      reason: "id_lookup_phrase_detected",
    };
  }

  if (!tabularEnabled) {
    return { route: "none", idLookupIntent: inactive, reason: "tabular_disabled" };
  }

  // Long-form requests already carry enough worksheet and per-row signal to
  // route deterministically, without unnecessary embedding I/O.
  if (shouldRouteToTabularQueryWorksheetWorkload(userRequest, tabularEnabled)) {
    return {
      route: "tabular",
      idLookupIntent: inactive,
      reason: "lexical_fallback",
    };
  }

  // Embeddings can only add a useful route when the active sheet supplies the
  // company rows that a terse request omitted from its text.
  if (!companyListPresent) {
    return {
      route: "none",
      idLookupIntent: inactive,
      reason: "no_company_list",
    };
  }

  if (!carriesClassifiableSignal(userRequest)) {
    return {
      route: "none",
      idLookupIntent: inactive,
      reason: "insufficient_signal",
    };
  }

  const intent = await classifyWorksheetColumnIntent(userRequest, {
    signal: input.signal,
  });
  const intentDiagnostics = {
    score: intent.score,
    confidence: intent.confidence,
    usedEmbeddings: intent.usedEmbeddings,
  };

  if (intent.usedEmbeddings && intent.confidence === "low") {
    return {
      route: "none",
      ...intentDiagnostics,
      idLookupIntent: inactive,
      reason: "low_confidence",
    };
  }

  if (intent.owner === "id_lookup" && intent.idLookupField) {
    return {
      route: "id_lookup",
      topicKey: intent.topicKey,
      topicName: intent.topicName,
      ...intentDiagnostics,
      inferredWorksheetList: true,
      idLookupIntent: {
        active: true,
        requestedFields: [intent.idLookupField],
      },
      reason: "id_lookup_topic_classified",
    };
  }

  if (intent.owner === "tabular") {
    return {
      route: "tabular",
      topicKey: intent.topicKey,
      topicName: intent.topicName,
      ...intentDiagnostics,
      idLookupIntent: inactive,
      reason: "topic_classified",
    };
  }

  // A confident non-routing verdict — formatting, modelling, or an aggregate
  // question — is respected. Anything else means semantic classification had
  // no signal after the deterministic alias and lexical checks both missed.
  if (intent.usedEmbeddings && intent.owner === "none") {
    return {
      route: "none",
      ...intentDiagnostics,
      idLookupIntent: inactive,
      reason: "classified_not_per_row",
    };
  }

  return {
    route: "none",
    ...intentDiagnostics,
    idLookupIntent: inactive,
    reason: "no_match",
  };
}
export function buildExecutorSkillAssembly(
  selectedSkills: string[] | undefined,
  brandId?: string | null,
): { skillIds: string[]; skillBlock: string } {
  const skillIds = withPushSkills(selectedSkills);
  const brandDefaults = getExcelBrandDefaults(brandId);
  const skillBlock = brandDefaults
    ? buildSkillContextBlock(skillIds, {
        formattingStyleOverride: brandDefaults.formattingStyleOverride,
      })
    : buildSkillContextBlock(skillIds);

  return { skillIds, skillBlock };
}
/**
 * Ingest one completed research-tool result into both existing source-card
 * accumulators and the run-scoped citation index used by workbook writes.
 * Citation ingestion is intentionally independent of source-card extraction:
 * a supported raw citation must remain usable even if it has no UI card.
 */
export function ingestResearchToolResult(
  toolOutput: string,
  retrievedSources: ExcelAgentSource[],
  retrievedCitations: unknown[],
  accumulators: ExecutorRunAccumulators,
  researchCitations?: unknown[],
  options: { persistCitationIndex?: boolean } = {},
): void {
  let parsed: Record<string, unknown> | undefined;
  try {
    const value = JSON.parse(toolOutput) as unknown;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      parsed = value as Record<string, unknown>;
    }
  } catch {
    // Non-JSON research outputs still retain their timeline preview.
  }

  const citations = Array.isArray(researchCitations)
    ? researchCitations
    : Array.isArray(parsed?.citations)
      ? parsed.citations
      : [];
  const sources = extractSourcesFromCitations(citations);
  if (sources.length > 0) retrievedSources.push(...sources);
  if (citations.length > 0) retrievedCitations.push(...citations);

  const response = { ...(parsed ?? {}), citations };
  if (options.persistCitationIndex !== false) {
    ingestAgentResponse((accumulators.citationNoteIndex ??= {}), response);
  }
  Object.assign((accumulators.retrievedCitedValuesByUuid ??= {}), extractCitedValuesById(response));
}
export function consumePendingShapeCorrectionAttempt(
  state: ExecutorVerificationState,
  toolName: string,
  parsedArgs: unknown
): string | undefined {
  const args = recordValue(parsedArgs);
  const stepId = typeof args.stepId === "string" ? args.stepId : null;
  const correction = findMatchingShapeCorrection(
    state.pendingShapeCorrections,
    toolName,
    stepId,
    parsedArgs
  );
  if (!correction) return undefined;
  if (correction.attempted) {
    return JSON.stringify({
      ok: false,
      applied: false,
      code: "range_matrix_shape_retry_exhausted",
      retryable: false,
      error:
        `The one allowed ${toolName} matrix-shape correction for ` +
        `${correction.address} has already been attempted.`,
      instruction:
        "Do not retry another write path. Mark the affected step failed or partial with the range evidence.",
    });
  }
  correction.attempted = true;
  return undefined;
}

export function findMatchingShapeCorrection(
  corrections: readonly PendingShapeCorrection[],
  toolName: string,
  stepId: string | null,
  parsedArgs: unknown
): PendingShapeCorrection | undefined {
  const candidates = corrections.filter(
    (candidate) => candidate.toolName === toolName && candidate.stepId === stepId
  );
  if (candidates.length === 0) return undefined;
  const targets = readbackContentTargetsFromWriteArgs(toolName, parsedArgs);
  const matches = (correction: PendingShapeCorrection): boolean => {
    const correctionRange = parseA1Range(correction.address);
    if (!correctionRange) return false;
    return targets.some((target) => {
      if (
        correction.sheetName &&
        normalizeSheetName(correction.sheetName) !== normalizeSheetName(target.sheetName)
      ) {
        return false;
      }
      const targetRange = parseA1Range(target.address);
      if (!targetRange || !rangeContains(correctionRange, targetRange)) {
        return false;
      }
      return (
        !correction.matrixField ||
        matrixFieldsForWriteTarget(toolName, parsedArgs, target.sheetName, target.address).has(
          correction.matrixField
        )
      );
    });
  };
  return candidates.find(matches);
}

export function matrixFieldsForWriteTarget(
  toolName: string,
  parsedArgs: unknown,
  sheetName: string,
  address: string
): Set<"values" | "formulas"> {
  if (toolName === "set_range_values") {
    return new Set<"values" | "formulas">(["values"]);
  }
  if (toolName === "set_range_formulas") {
    return new Set<"values" | "formulas">(["formulas"]);
  }
  const fields = new Set<"values" | "formulas">();
  const args = recordValue(parsedArgs);
  const modifications = recordValue(args.modifications);
  const worksheets = Array.isArray(modifications.worksheets) ? modifications.worksheets : [];
  for (const worksheetValue of worksheets) {
    const worksheet = recordValue(worksheetValue);
    const candidateSheet = stringValue(worksheet.sheetName) ?? stringValue(args.targetSheet);
    if (normalizeSheetName(candidateSheet) !== normalizeSheetName(sheetName)) {
      continue;
    }
    const ranges = Array.isArray(worksheet.ranges) ? worksheet.ranges : [];
    for (const rangeValue of ranges) {
      const range = recordValue(rangeValue);
      if (
        range.action !== "fill" ||
        normalizeLocalAddressKey(stringValue(range.address) ?? "") !==
          normalizeLocalAddressKey(address)
      ) {
        continue;
      }
      if (range.values !== undefined) fields.add("values");
      if (range.formulas !== undefined) fields.add("formulas");
    }
  }
  return fields;
}

export interface AmbiguousGranularRecoveryOptions {
  toolName: string;
  parsedArgs: unknown;
  result: ToolCallDetailedResult;
  executors: Record<string, RuntimeToolExecutor>;
  signal?: AbortSignal;
  pinnedWorkbookName?: string;
  recoveryKeys: Set<string>;
  accumulators: ExecutorRunAccumulators;
  onEvent?: (event: ExecutorRunEvent) => void;
}

export function runtimeToolOutput(result: string | ToolCallDetailedResult): string {
  return typeof result === "string" ? result : result.output;
}

/**
 * Build the one-shot recovery identity for a granular range write.
 *
 * @param toolName - Candidate executor tool name.
 * @param parsedArgs - Parsed arguments containing sheet and address.
 * @returns A normalized key for granular writes, otherwise `undefined`.
 */
export function granularRecoveryKey(
  toolName: string,
  parsedArgs: unknown
): string | undefined {
  if (toolName !== "set_range_values" && toolName !== "set_range_formulas") {
    return undefined;
  }
  const args = recordValue(parsedArgs);
  const sheetName = stringValue(args.sheetName);
  const address = stringValue(args.address);
  if (!sheetName || !address) return undefined;
  return [
    toolName,
    stringValue(args.stepId) ?? "",
    normalizeSheetName(sheetName) ?? sheetName,
    normalizeLocalAddressKey(address),
  ].join("|");
}

export async function recoverAmbiguousGranularRangeWrite(
  options: AmbiguousGranularRecoveryOptions
): Promise<ToolCallDetailedResult> {
  const {
    toolName,
    parsedArgs,
    result,
    executors,
    signal,
    pinnedWorkbookName,
    recoveryKeys,
    accumulators,
    onEvent,
  } = options;
  if (toolName !== "set_range_values" && toolName !== "set_range_formulas") {
    return result;
  }
  const output = safeParseJson(result.output);
  const outputRecord = recordValue(output);
  const failedActions = Array.isArray(outputRecord.failedActions) ? outputRecord.failedActions : [];
  if (
    outputRecord.applied !== false ||
    outputRecord.retryReason !== undefined ||
    outputRecord.code === "range_matrix_shape_mismatch" ||
    (failedActions.length === 0 && outputRecord.ambiguousWrite !== true)
  ) {
    return result;
  }

  const args = recordValue(parsedArgs);
  const sheetName = stringValue(args.sheetName);
  const address = stringValue(args.address);
  if (!sheetName || !address) return result;
  const recoveryKey = granularRecoveryKey(toolName, parsedArgs);
  if (!recoveryKey) return result;
  if (recoveryKeys.has(recoveryKey)) {
    return {
      ...result,
      output: JSON.stringify({
        ...outputRecord,
        recoveryExhausted: true,
        retryable: false,
        instruction: "The one readback-first recovery round for this range is already exhausted.",
      }),
    };
  }
  recoveryKeys.add(recoveryKey);

  const readExecutor = executors.read_range;
  const writeExecutor = executors[toolName];
  if (!readExecutor || !writeExecutor) return result;
  throwIfAborted(signal);
  onEvent?.({
    type: "status",
    message: `Checking ${sheetName}!${address} before bounded write recovery.`,
  });
  const readArgs = {
    sheetName,
    address,
    workbookName: pinnedWorkbookName ?? null,
    format: "json",
    includeFormulas: true,
    includeFormats: false,
  };
  const readOutput = safeParseJson(runtimeToolOutput(await readExecutor(readArgs, signal)));
  const readRecord = recordValue(readOutput);
  const target = readbackContentTargetsFromWriteArgs(toolName, parsedArgs)[0];
  if (!target) return result;
  if (
    readRecord.error ||
    readRecord.truncated === true ||
    !hasDenseReadbackEvidence(target, readRecord)
  ) {
    return {
      ...result,
      output: JSON.stringify({
        ...outputRecord,
        recoveryExhausted: true,
        retryable: false,
        recoveryReadback: readOutput,
        recoveryError:
          "The exact dense formula/value read-back was unavailable or truncated, so the runtime did not replay an ambiguous write.",
      }),
    };
  }
  const evaluation = evaluateWorksheetContentReadback(target, readArgs, readOutput);
  const missingKeys = new Set(
    evaluation.missingCells.map((cell) => `${cell.kind}|${normalizeLocalAddressKey(cell.address)}`)
  );
  const confirmedCells = target.cells.filter(
    (cell) => !missingKeys.has(`${cell.kind}|${normalizeLocalAddressKey(cell.address)}`)
  );
  const confirmedAddresses = recordReadbackConfirmedGranularWrite(
    accumulators,
    toolName,
    args,
    target,
    confirmedCells
  );
  if (evaluation.complete) {
    return {
      ...result,
      output: JSON.stringify({
        ...outputRecord,
        ok: true,
        applied: true,
        error: undefined,
        failedActions: undefined,
        initialFailedActions: failedActions,
        editedCells: confirmedAddresses,
        recoveredByReadback: true,
        recoveryReadback: readOutput,
      }),
    };
  }
  const segments = buildMissingRowSegments(
    evaluation.missingCells,
    toolName === "set_range_formulas" ? "formulas" : "values"
  );
  if (segments.length === 0) {
    return {
      ...result,
      output: JSON.stringify({
        ...outputRecord,
        recoveryExhausted: true,
        retryable: false,
        recoveryReadback: readOutput,
        recoveryError: evaluation.reason ?? "Read-back did not prove full target coverage.",
      }),
    };
  }

  const retryResults: Array<{
    address: string;
    output: unknown;
    applied: boolean;
  }> = [];
  for (const segment of segments) {
    throwIfAborted(signal);
    const retryArgs = {
      stepId: typeof args.stepId === "string" && args.stepId.trim() ? args.stepId : null,
      sheetName,
      address: segment.address,
      [segment.field]: segment.matrix,
    };
    const retryOutput = safeParseJson(runtimeToolOutput(await writeExecutor(retryArgs, signal)));
    retryResults.push({
      address: segment.address,
      output: retryOutput,
      applied: recordValue(retryOutput).applied === true,
    });
  }
  const allApplied = retryResults.every((retry) => retry.applied);
  return {
    ...result,
    output: JSON.stringify({
      ...outputRecord,
      ok: allApplied,
      applied: allApplied,
      error: allApplied ? undefined : "One or more bounded missing-coordinate retries failed.",
      failedActions: allApplied
        ? undefined
        : retryResults
            .filter((retry) => !retry.applied)
            .map((retry) => `range ${retry.address} bounded recovery`),
      initialFailedActions: failedActions,
      editedCells: Array.from(
        new Set([
          ...confirmedAddresses,
          ...retryResults.filter((retry) => retry.applied).map((retry) => retry.address),
        ])
      ),
      recoveredByRetry: allApplied,
      recoveryExhausted: true,
      recoveryReadback: readOutput,
      recoveryAttempts: retryResults,
    }),
  };
}

export function recordReadbackConfirmedGranularWrite(
  accumulators: ExecutorRunAccumulators,
  toolName: string,
  args: Record<string, unknown>,
  target: ExecutorContentReadbackTarget,
  confirmedCells: readonly ExecutorExpectedContentCell[] = target.cells
): string[] {
  const sheetName = stringValue(args.sheetName);
  const address = stringValue(args.address);
  if (!sheetName || !address || confirmedCells.length === 0) return [];
  const isFormulaWrite =
    toolName === "set_range_formulas" || confirmedCells.some((cell) => cell.kind === "formula");
  const field = toolName === "set_range_formulas" ? ("formulas" as const) : ("values" as const);
  const isFullTarget =
    confirmedCells.length === target.cells.length &&
    target.cells.every((cell) =>
      confirmedCells.some(
        (confirmed) =>
          confirmed.kind === cell.kind &&
          normalizeLocalAddressKey(confirmed.address) === normalizeLocalAddressKey(cell.address)
      )
    );
  const segments: MissingRowSegment[] = isFullTarget
    ? [
        {
          address,
          field,
          matrix:
            field === "formulas"
              ? (args.formulas as (string | null)[][])
              : (args.values as (string | number | boolean | null)[][]),
        },
      ]
    : buildMissingRowSegments(confirmedCells, field);
  const confirmedAddresses = segments.map((segment) => segment.address);
  for (const confirmedAddress of confirmedAddresses) {
    if (!accumulators.editedCells.includes(confirmedAddress)) {
      accumulators.editedCells.push(confirmedAddress);
    }
  }
  accumulators.lastModifications = {
    worksheets: [
      {
        sheetName,
        ranges: segments.map((segment) => ({
          address: segment.address,
          action: "fill" as const,
          [segment.field]: segment.matrix,
        })),
      },
    ],
    message:
      `Confirmed ${confirmedAddresses.map((entry) => `${sheetName}!${entry}`).join(", ")} ` +
      "from read-back after an ambiguous host acknowledgement.",
  };
  recordColumnSizingIntent(accumulators, accumulators.lastModifications);
  accumulators.autofitSheetNames ??= new Set<string>();
  accumulators.autofitTargets ??= [];
  accumulators.autofitSheetNames.add(sheetName);
  for (const confirmedAddress of confirmedAddresses) {
    if (
      !accumulators.autofitTargets.some(
        (entry) =>
          normalizeSheetName(entry.sheetName) === normalizeSheetName(sheetName) &&
          normalizeLocalAddressKey(entry.address) === normalizeLocalAddressKey(confirmedAddress)
      )
    ) {
      accumulators.autofitTargets.push({
        sheetName,
        address: confirmedAddress,
      });
    }
  }
  if (isFormulaWrite) {
    accumulators.formulaWriteTargets ??= [];
    for (const confirmedAddress of confirmedAddresses) {
      if (
        !accumulators.formulaWriteTargets.some(
          (entry) =>
            normalizeSheetName(entry.sheetName) === normalizeSheetName(sheetName) &&
            normalizeLocalAddressKey(entry.address) === normalizeLocalAddressKey(confirmedAddress)
        )
      ) {
        accumulators.formulaWriteTargets.push({
          sheetName,
          address: confirmedAddress,
        });
      }
    }
  }
  const fdsCells = confirmedCells.filter(
    (cell) => cell.kind === "formula" && isFdsBoundFormula(cell.expected)
  );
  if (fdsCells.length > 0) {
    accumulators.fdsRefreshSheets.add(sheetName);
    accumulators.fdsFormulaWriteTargets ??= [];
    for (const cell of fdsCells) {
      if (
        !accumulators.fdsFormulaWriteTargets.some(
          (entry) =>
            normalizeSheetName(entry.sheetName) === normalizeSheetName(sheetName) &&
            normalizeLocalAddressKey(entry.address) === normalizeLocalAddressKey(cell.address)
        )
      ) {
        accumulators.fdsFormulaWriteTargets.push({
          sheetName,
          address: cell.address,
        });
      }
    }
  }
  return confirmedAddresses;
}

export interface MissingRowSegment {
  address: string;
  field: "values" | "formulas";
  matrix: Array<Array<string | number | boolean | null>>;
}

export function buildMissingRowSegments(
  missingCells: readonly ExecutorExpectedContentCell[],
  field: "values" | "formulas"
): MissingRowSegment[] {
  const byRow = new Map<
    number,
    Array<{ row: number; column: number; cell: ExecutorExpectedContentCell }>
  >();
  for (const cell of missingCells) {
    const position = parseA1Cell(cell.address);
    if (!position) continue;
    const entries = byRow.get(position.row) ?? [];
    entries.push({ ...position, cell });
    byRow.set(position.row, entries);
  }
  const segments: MissingRowSegment[] = [];
  for (const [row, entries] of Array.from(byRow.entries()).sort(
    ([left], [right]) => left - right
  )) {
    const sorted = entries.sort((left, right) => left.column - right.column);
    let current: typeof sorted = [];
    const flush = (): void => {
      if (current.length === 0) return;
      const start = current[0];
      const end = current[current.length - 1];
      const address =
        start.column === end.column
          ? formatA1Cell(row, start.column)
          : `${formatA1Cell(row, start.column)}:${formatA1Cell(row, end.column)}`;
      segments.push({
        address,
        field,
        matrix: [current.map((entry) => entry.cell.expected)],
      });
      current = [];
    };
    for (const entry of sorted) {
      if (current.length > 0 && entry.column !== current[current.length - 1].column + 1) {
        flush();
      }
      current.push(entry);
    }
    flush();
  }
  return segments;
}

export function recordValue(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

export const MAX_CHECKPOINT_FORMULA_CACHE_ENTRIES = 200;
export const FORMULA_CONTINUATION_BATCH_SIZE = 50;
export const MAX_FORMULA_RESOLUTION_ATTEMPTS_PER_QUERY = 2;

export interface GenerateFormulaCachePlan {
  queries: string[];
  missingQueries: string[];
  exhaustedQueries: string[];
  dispatchArgs: Record<string, unknown>;
}

export function prepareGenerateFormulaCachePlan(
  args: unknown,
  cache: ReadonlyMap<string, Record<string, unknown>>,
  attempts: ReadonlyMap<string, number>
): GenerateFormulaCachePlan | null {
  const input = recordValue(args);
  if (!Array.isArray(input.queries)) return null;
  const queries = input.queries.map((query) => (typeof query === "string" ? query.trim() : ""));
  const exhaustedQueries = queries.filter(
    (query) =>
      !!query &&
      !cache.has(query) &&
      (attempts.get(query) ?? 0) >= MAX_FORMULA_RESOLUTION_ATTEMPTS_PER_QUERY
  );
  const exhausted = new Set(exhaustedQueries);
  const missingQueries = queries.filter(
    (query) => (!query || !cache.has(query)) && !exhausted.has(query)
  );
  return {
    queries,
    missingQueries,
    exhaustedQueries,
    dispatchArgs:
      missingQueries.length === queries.length ? input : { ...input, queries: missingQueries },
  };
}

export function buildGenerateFormulaCacheEnvelope(
  results: Array<Record<string, unknown>>,
  cacheHitCount: number
): Record<string, unknown> {
  const unresolvedQueries = results
    .filter((result) => typeof result.error === "string" && result.dataUnavailable !== true)
    .map((result) => String(result.query ?? ""));
  const retryableQueries = results
    .filter((result) => typeof result.error === "string" && result.retryable !== false)
    .map((result) => String(result.query ?? ""));
  // Surfaced separately from `unresolvedQueries` so the model can see at a
  // glance which metrics are finished-as-unavailable rather than outstanding.
  const unavailableQueries = results
    .filter((result) => result.dataUnavailable === true)
    .map((result) => String(result.query ?? ""));
  const resolvedCount = results.length - unresolvedQueries.length;
  const status =
    unresolvedQueries.length === 0 ? "complete" : resolvedCount > 0 ? "partial" : "failed";
  const continuationBatches: string[][] = [];
  for (let start = 0; start < retryableQueries.length; start += FORMULA_CONTINUATION_BATCH_SIZE) {
    continuationBatches.push(
      retryableQueries.slice(start, start + FORMULA_CONTINUATION_BATCH_SIZE)
    );
  }
  return {
    ok: status === "complete",
    status,
    partial: status === "partial",
    retryable: retryableQueries.length > 0,
    results,
    summary: {
      requestedCount: results.length,
      resolvedCount,
      failedCount: unresolvedQueries.length,
      unavailableCount: unavailableQueries.length,
      cacheHitCount,
    },
    unresolvedQueries,
    ...(unavailableQueries.length > 0 ? { unavailableQueries } : {}),
    continuationBatches,
  };
}

export function mergeGenerateFormulaCacheResult(
  plan: GenerateFormulaCachePlan,
  result: ToolCallDetailedResult | null,
  cache: Map<string, Record<string, unknown>>,
  attempts: ReadonlyMap<string, number>
): ToolCallDetailedResult {
  const parsed = result ? recordValue(safeParseJson(result.output)) : {};
  const freshResults = Array.isArray(parsed.results) ? parsed.results.map(recordValue) : [];
  const freshByQuery = new Map<string, Record<string, unknown>>();
  for (const entry of freshResults) {
    if (typeof entry.query === "string") {
      freshByQuery.set(entry.query.trim(), entry);
    }
  }
  let cacheHitCount = 0;
  const mergedResults = plan.queries.map((query) => {
    const cached = cache.get(query);
    if (cached) {
      cacheHitCount += 1;
      return { ...cached, query, cacheHit: true };
    }
    if (plan.exhaustedQueries.includes(query)) {
      return {
        query,
        error: "Formula resolution retry budget exhausted after one continuation attempt.",
        retryable: false,
      };
    }
    const fresh = freshByQuery.get(query);
    if (fresh) {
      if (
        typeof fresh.error === "string" &&
        (parsed.retryable === false ||
          (query && (attempts.get(query) ?? 0) >= MAX_FORMULA_RESOLUTION_ATTEMPTS_PER_QUERY))
      ) {
        return { ...fresh, retryable: false };
      }
      return fresh;
    }
    return {
      query,
      error:
        typeof parsed.error === "string" ? parsed.error : "TTF returned no result for this query.",
      retryable: !query || (attempts.get(query) ?? 0) < MAX_FORMULA_RESOLUTION_ATTEMPTS_PER_QUERY,
    };
  });
  for (const entry of mergedResults) {
    // A definitive "no such data" verdict is cached alongside successes: it is
    // as final as a formula, and caching it keeps a resubmitted query from
    // spending another TTF round trip to learn the same thing.
    const isCacheableOutcome =
      (typeof entry.error !== "string" &&
        (typeof entry.formula === "string" || typeof entry.formula_template === "string")) ||
      entry.dataUnavailable === true;
    if (typeof entry.query === "string" && isCacheableOutcome) {
      const cachedEntry = { ...entry };
      delete cachedEntry.cacheHit;
      cache.delete(entry.query);
      cache.set(entry.query, cachedEntry);
      while (cache.size > MAX_CHECKPOINT_FORMULA_CACHE_ENTRIES) {
        const oldest = cache.keys().next().value;
        if (typeof oldest !== "string") break;
        cache.delete(oldest);
      }
    }
  }
  return {
    ...(result ?? {}),
    output: JSON.stringify(buildGenerateFormulaCacheEnvelope(mergedResults, cacheHitCount)),
  };
}

/**
 * Build a deterministic semantic fingerprint for a guarded workbook write.
 *
 * @param toolName - Provider-visible executor tool name.
 * @param args - Parsed write arguments; synthetic `stepId` is ignored.
 * @returns A compact deterministic fingerprint.
 */
export function stableWriteFingerprint(toolName: string, args: unknown): string {
  const fingerprintArgs =
    args !== null && typeof args === "object" && !Array.isArray(args)
      ? Object.fromEntries(
          Object.entries(args as Record<string, unknown>).filter(([key]) => key !== "stepId")
        )
      : args;
  const canonical = canonicalJson(fingerprintArgs);
  return [
    toolName,
    canonical.length,
    hashCanonicalString(canonical, 0x811c9dc5),
    hashCanonicalString(canonical, 0x9e3779b9),
  ].join(":");
}

export function canonicalJson(value: unknown): string {
  if (value === null) return "null";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(canonicalJson).join(",")}]`;
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record)
      .filter((key) => record[key] !== undefined)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(String(value));
}

export function hashCanonicalString(value: string, seed: number): string {
  let hash = seed >>> 0;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, "0");
}

/**
 * Determine whether a host observation confirms an unambiguous write.
 *
 * @param output - Parsed tool observation.
 * @returns Whether no error, failure, or skipped action is present.
 */
export function isConfirmedWriteResult(output: Record<string, unknown>): boolean {
  if (
    output.applied === false ||
    output.error ||
    (Array.isArray(output.failedActions) && output.failedActions.length > 0) ||
    (Array.isArray(output.skippedActions) && output.skippedActions.length > 0)
  ) {
    return false;
  }
  return output.applied === true || output.ok === true;
}

export function isResolvedHostAcknowledgement(output: Record<string, unknown>): boolean {
  return (
    output.applied === true &&
    !output.error &&
    output.ambiguousWrite !== true &&
    output.retryReason === undefined &&
    !(Array.isArray(output.failedActions) && output.failedActions.length > 0) &&
    !(Array.isArray(output.skippedActions) && output.skippedActions.length > 0)
  );
}

export function shouldCheckpointBlockedWrite(output: Record<string, unknown>): boolean {
  if (output.code === "range_matrix_shape_mismatch") return false;
  if (output.ambiguousWrite === true) return true;
  if (isResolvedHostAcknowledgement(output)) return false;
  const listed = (key: string): boolean => {
    const value = output[key];
    return Array.isArray(value) && value.length > 0;
  };
  const retryMayFollowDispatch =
    output.retryReason !== undefined && output.hostDispatchAttempted !== false;
  const evidenceOfContact =
    retryMayFollowDispatch || listed("editedCells") || listed("failedActions");
  // A tool that reports it applied nothing and names neither confirmed edits
  // nor attempted failures has no mutation that might have half landed.
  // Declared skips and explicitly identified pre-dispatch host-ready pauses are
  // no-attempts, not evidence of an ambiguous write. A retry without that
  // explicit marker fails closed because a first operation may have landed.
  // Checkpointing it would refuse the identical call on the next run as a
  // possible partial write, which is the one thing a no-op cannot be.
  if (output.applied === false && !evidenceOfContact) return false;
  return output.applied === true || output.ok === true || evidenceOfContact;
}

/**
 * Compare a candidate write with the unresolved write retained by the run.
 *
 * @param blockedOperation - Existing unresolved write, when any.
 * @param toolName - Candidate tool name.
 * @param args - Candidate parsed arguments.
 * @returns Whether both writes have the same semantic fingerprint.
 */
export function blockedOperationExactlyMatches(
  blockedOperation: ExecutorBlockedOperation | undefined,
  toolName: string,
  args: unknown
): boolean {
  return (
    blockedOperation?.toolName === toolName &&
    stableWriteFingerprint(toolName, blockedOperation.args) ===
      stableWriteFingerprint(toolName, args)
  );
}

/**
 * Whether a pending write would touch the cells whose acknowledgement is still
 * unresolved.
 *
 * Only overlapping writes are dangerous: replaying them could double-apply, or
 * race the read-back that is meant to establish what actually landed. Writes
 * to other sheets or other ranges are independent, and blocking them just
 * serializes the run behind one uncertain acknowledgement. A write whose
 * target cannot be determined is treated as overlapping, since the gate should
 * fail closed. Focus-only activation and bounded formatting/layout calls are
 * exceptions because they cannot double-apply cell content.
 *
 * @param blockedOperation - Unresolved write retained by the run.
 * @param toolName - Candidate write tool name.
 * @param args - Candidate parsed write arguments.
 * @returns Whether ranges overlap or cannot be safely distinguished.
 */
export function writeOverlapsBlockedOperation(
  blockedOperation: ExecutorBlockedOperation,
  toolName: string,
  args: unknown
): boolean {
  const blockedIsAnnotation = ANNOTATION_WRITE_TOOLS.has(blockedOperation.toolName);
  const candidateIsAnnotation = ANNOTATION_WRITE_TOOLS.has(toolName);
  if (blockedIsAnnotation || candidateIsAnnotation) {
    if (blockedIsAnnotation && candidateIsAnnotation) {
      return annotationTargetsOverlap(
        annotationTargetsFromWriteArgs(
          blockedOperation.toolName,
          blockedOperation.args,
        ),
        annotationTargetsFromWriteArgs(toolName, args),
      );
    }
    // Notes and comments are a separate mutation domain from workbook cells.
    // The exact-call replay guard still blocks the ambiguous annotation, while
    // writes in the other mutation domain may proceed independently.
    return false;
  }
  const blockedTargets = readbackContentTargetsFromWriteArgs(
    blockedOperation.toolName,
    blockedOperation.args
  );
  const candidateTargets = readbackContentTargetsFromWriteArgs(toolName, args);
  const candidate = recordValue(args);
  if (
    toolName === "activate_sheet" ||
    (toolName === "manage_worksheet" && candidate.operation === "activate")
  ) {
    return false;
  }
  const blockedArgs = recordValue(blockedOperation.args);
  if (
    blockedOperation.toolName === "format_range" ||
    blockedOperation.toolName === "layout_range" ||
    blockedOperation.toolName === "activate_sheet" ||
    (blockedOperation.toolName === "manage_worksheet" &&
      blockedArgs.operation === "activate")
  ) {
    // A lost acknowledgement for formatting, sizing, or focus cannot have
    // partially changed cell content. The exact-call replay guard still blocks
    // repeating that operation, but unrelated content writes remain safe.
    return false;
  }
  if (toolName === "format_range" || toolName === "layout_range") {
    if (blockedWriteIsCoordinateStableAndContentIndependent(blockedOperation)) {
      return false;
    }
    const candidateSheet = normalizeSheetName(candidate.sheetName);
    const blockedSheets = affectedSheetNamesFromWrite(
      blockedOperation.toolName,
      blockedOperation.args,
    );
    if (
      candidateSheet &&
      blockedSheets.length > 0 &&
      !blockedSheets.includes(candidateSheet)
    ) {
      return false;
    }
    const candidateRange =
      typeof candidate.address === "string"
        ? parseA1Range(candidate.address)
        : undefined;
    if (
      candidateSheet &&
      candidateRange &&
      blockedTargets.length > 0 &&
      blockedTargets.every((blocked) => {
        if (normalizeSheetName(blocked.sheetName) !== candidateSheet) return true;
        const blockedRange = parseA1Range(blocked.address);
        return Boolean(blockedRange && !rangesOverlap(blockedRange, candidateRange));
      })
    ) {
      return false;
    }
    return true;
  }
  if (blockedTargets.length === 0 || candidateTargets.length === 0) return true;
  return candidateTargets.some((candidate) => {
    const candidateRange = parseA1Range(candidate.address);
    if (!candidateRange) return true;
    return blockedTargets.some((blocked) => {
      if (normalizeSheetName(blocked.sheetName) !== normalizeSheetName(candidate.sheetName)) {
        return false;
      }
      const blockedRange = parseA1Range(blocked.address);
      return !blockedRange || rangesOverlap(blockedRange, candidateRange);
    });
  });
}

/** Sheet-local cell or range targeted by an annotation mutation. */
interface AnnotationTarget {
  /** Worksheet containing the annotation target. */
  sheetName: string;
  /** A1 address supplied to the annotation tool. */
  address: string;
}

/**
 * Extract comparable worksheet targets from one annotation write.
 *
 * @param toolName - Annotation mutation tool name.
 * @param rawArgs - Untrusted tool arguments.
 * @returns Parsed targets, or an empty list when no safe target is available.
 */
function annotationTargetsFromWriteArgs(
  toolName: string,
  rawArgs: unknown,
): AnnotationTarget[] {
  if (!ANNOTATION_WRITE_TOOLS.has(toolName)) return [];
  const args = recordValue(rawArgs);
  const targets: AnnotationTarget[] = [];
  const add = (sheetValue: unknown, addressValue: unknown): void => {
    if (typeof sheetValue !== "string" || typeof addressValue !== "string") return;
    const sheetName = sheetValue.trim();
    const address = addressValue.trim();
    if (sheetName && address) targets.push({ sheetName, address });
  };

  if (toolName === "set_citations") {
    for (const citation of Array.isArray(args.citations) ? args.citations : []) {
      const entry = recordValue(citation);
      add(entry.sheetName, entry.address);
    }
    return targets;
  }

  if (toolName === "set_comment" || toolName === "set_note") {
    for (const entryValue of Array.isArray(args.entries) ? args.entries : []) {
      add(args.sheetName, recordValue(entryValue).address);
    }
    return targets;
  }

  for (const address of Array.isArray(args.addresses) ? args.addresses : []) {
    add(args.sheetName, address);
  }
  return targets;
}

/**
 * Determine whether two annotation target sets may touch the same cells.
 *
 * @param blockedTargets - Targets from the unresolved annotation write.
 * @param candidateTargets - Targets from the proposed annotation write.
 * @returns `true` when overlap exists or either set cannot be parsed safely.
 */
function annotationTargetsOverlap(
  blockedTargets: AnnotationTarget[],
  candidateTargets: AnnotationTarget[],
): boolean {
  if (blockedTargets.length === 0 || candidateTargets.length === 0) return true;
  return candidateTargets.some((candidate) => {
    const candidateRange = parseA1Range(candidate.address);
    if (!candidateRange) return true;
    return blockedTargets.some((blocked) => {
      if (normalizeSheetName(blocked.sheetName) !== normalizeSheetName(candidate.sheetName)) {
        return false;
      }
      const blockedRange = parseA1Range(blocked.address);
      return !blockedRange || rangesOverlap(blockedRange, candidateRange);
    });
  });
}

/**
 * Map an annotation mutation to the exact read tool that can resolve it.
 *
 * @param toolName - Annotation mutation tool name.
 * @returns The annotation kind, or `undefined` for non-annotation writes.
 */
function annotationKindForWrite(
  toolName: string,
): "comment" | "note" | undefined {
  if (toolName === "set_comment" || toolName === "delete_comment") return "comment";
  if (
    toolName === "set_note" ||
    toolName === "delete_note" ||
    toolName === "set_citations"
  ) {
    return "note";
  }
  return undefined;
}

/**
 * Resolve a single ambiguous annotation after an exact successful host read.
 *
 * A read does not prove whether the original operation succeeded, but it does
 * establish the current state. That is enough to permit a deliberate retry or
 * replacement without risking a blind replay. Multi-cell batches remain
 * blocked until every target can be accounted for together.
 */
export function annotationReadResolvesBlockedOperation(
  blockedOperation: ExecutorBlockedOperation,
  readToolName: string,
  readArgs: unknown,
  readOutput: unknown,
): boolean {
  const kind = annotationKindForWrite(blockedOperation.toolName);
  const readKind =
    readToolName === "get_comment"
      ? "comment"
      : readToolName === "get_note"
        ? "note"
        : undefined;
  if (!kind || readKind !== kind || recordValue(readOutput).ok !== true) return false;

  const blockedTargets = annotationTargetsFromWriteArgs(
    blockedOperation.toolName,
    blockedOperation.args,
  );
  if (blockedTargets.length !== 1) return false;

  const args = recordValue(readArgs);
  const sheetName = normalizeSheetName(args.sheetName);
  const address =
    typeof args.address === "string" ? args.address.trim().toLowerCase() : "";
  const blocked = blockedTargets[0];
  const blockedRange = parseA1Range(blocked.address);
  const readRange = parseA1Range(address);
  return (
    !!sheetName &&
    !!blockedRange &&
    !!readRange &&
    normalizeSheetName(blocked.sheetName) === sheetName &&
    blockedRange.startRow === readRange.startRow &&
    blockedRange.endRow === readRange.endRow &&
    blockedRange.startColumn === readRange.startColumn &&
    blockedRange.endColumn === readRange.endColumn
  );
}

export function affectedSheetNamesFromWrite(
  toolName: string,
  rawArgs: unknown,
): string[] {
  const args = recordValue(rawArgs);
  const names = new Set<string>();
  const add = (value: unknown): void => {
    const normalized = normalizeSheetName(value);
    if (normalized) names.add(normalized);
  };
  add(args.sheetName);
  add(args.destinationSheetName);
  if (toolName === "copy_worksheet") add(args.sourceSheetName);
  if (toolName === "manage_worksheet") add(args.name);
  const modifications =
    args.modifications !== null && typeof args.modifications === "object"
      ? recordValue(args.modifications)
      : args;
  for (const worksheetValue of Array.isArray(modifications.worksheets)
    ? modifications.worksheets
    : []) {
    add(recordValue(worksheetValue).sheetName);
  }
  for (const sheetValue of Array.isArray(modifications.sheets)
    ? modifications.sheets
    : []) {
    add(recordValue(sheetValue).name);
  }
  return Array.from(names);
}

export function blockedWriteIsCoordinateStableAndContentIndependent(
  blockedOperation: ExecutorBlockedOperation,
): boolean {
  if (
    blockedOperation.toolName === "format_range" ||
    blockedOperation.toolName === "layout_range" ||
    blockedOperation.toolName === "activate_sheet"
  ) {
    return true;
  }
  if (blockedOperation.toolName === "manage_worksheet") {
    return recordValue(blockedOperation.args).operation === "activate";
  }
  if (blockedOperation.toolName !== "modify_workbook") return false;

  const args = recordValue(blockedOperation.args);
  const modifications =
    args.modifications !== null && typeof args.modifications === "object"
      ? recordValue(args.modifications)
      : args;
  let sawMutation = false;
  const safeRangeActions = new Set([
    "format",
    "autofit",
    "resize",
    "hideColumns",
    "showColumns",
    "hideRows",
    "showRows",
  ]);
  for (const worksheetValue of Array.isArray(modifications.worksheets)
    ? modifications.worksheets
    : []) {
    const worksheet = recordValue(worksheetValue);
    if (
      (Array.isArray(worksheet.cells) && worksheet.cells.length > 0) ||
      (Array.isArray(worksheet.tables) && worksheet.tables.length > 0) ||
      (Array.isArray(worksheet.logos) && worksheet.logos.length > 0)
    ) {
      return false;
    }
    for (const rangeValue of Array.isArray(worksheet.ranges) ? worksheet.ranges : []) {
      sawMutation = true;
      if (!safeRangeActions.has(String(recordValue(rangeValue).action ?? ""))) {
        return false;
      }
    }
  }
  for (const sheetValue of Array.isArray(modifications.sheets) ? modifications.sheets : []) {
    sawMutation = true;
    if (recordValue(sheetValue).action !== "activate") return false;
  }
  if (
    Array.isArray(modifications.workbookOperations) &&
    modifications.workbookOperations.length > 0
  ) {
    return false;
  }
  return sawMutation;
}

/**
 * Validate a narrower retry against read-back-proven missing cells.
 *
 * @param blockedOperation - Unresolved write with read-back evidence.
 * @param toolName - Candidate granular write tool.
 * @param args - Candidate parsed write arguments.
 * @returns Remediated cells when safe, otherwise `undefined`.
 */
export function narrowGranularRemediationCells(
  blockedOperation: ExecutorBlockedOperation,
  toolName: string,
  args: unknown
): string[] | undefined {
  if (
    blockedOperation.toolName !== toolName ||
    blockedOperation.readbackObserved !== true ||
    !Array.isArray(blockedOperation.unresolvedCells) ||
    blockedOperation.unresolvedCells.length === 0 ||
    (toolName !== "set_range_values" && toolName !== "set_range_formulas")
  ) {
    return undefined;
  }
  const blockedStepId = stringValue(recordValue(blockedOperation.args).stepId);
  const candidateStepId = stringValue(recordValue(args).stepId);
  if ((blockedStepId ?? "") !== (candidateStepId ?? "")) return undefined;
  const blockedTarget = readbackContentTargetsFromWriteArgs(toolName, blockedOperation.args)[0];
  const candidateTarget = readbackContentTargetsFromWriteArgs(toolName, args)[0];
  if (
    !blockedTarget ||
    !candidateTarget ||
    normalizeSheetName(blockedTarget.sheetName) !== normalizeSheetName(candidateTarget.sheetName)
  ) {
    return undefined;
  }
  const unresolved = new Set(blockedOperation.unresolvedCells.map(normalizeLocalAddressKey));
  const expectedByAddress = new Map(
    blockedTarget.cells.map((cell) => [normalizeLocalAddressKey(cell.address), cell])
  );
  const matches = candidateTarget.cells.every((candidate) => {
    const key = normalizeLocalAddressKey(candidate.address);
    const expected = expectedByAddress.get(key);
    return (
      unresolved.has(key) &&
      !!expected &&
      expected.kind === candidate.kind &&
      Object.is(expected.expected, candidate.expected)
    );
  });
  return matches && candidateTarget.cells.length > 0
    ? candidateTarget.cells.map((cell) => cell.address)
    : undefined;
}

export function blockedGranularEvidenceFromResult(
  toolName: string,
  args: unknown,
  output: Record<string, unknown>
):
  | Pick<
      ExecutorBlockedOperation,
      "readbackObserved" | "unresolvedCells"
    >
  | Record<string, never> {
  if (toolName !== "set_range_values" && toolName !== "set_range_formulas") {
    return {};
  }
  const target = readbackContentTargetsFromWriteArgs(toolName, args)[0];
  const readback = recordValue(output.recoveryReadback);
  if (!target || !hasDenseReadbackEvidence(target, readback)) return {};
  const evaluation = evaluateWorksheetContentReadback(
    target,
    { format: "json", includeFormulas: true },
    readback
  );
  const editedRanges = Array.isArray(output.editedCells)
    ? output.editedCells
        .filter((address): address is string => typeof address === "string")
        .map(parseA1Range)
        .filter((range): range is ParsedA1Range => !!range)
    : [];
  const unresolvedCells = evaluation.missingCells
    .filter((cell) => {
      const position = parseA1Cell(cell.address);
      return (
        !position ||
        !editedRanges.some(
          (range) =>
            position.row >= range.startRow &&
            position.row <= range.endRow &&
            position.column >= range.startColumn &&
            position.column <= range.endColumn
        )
      );
    })
    .map((cell) => cell.address);
  return { readbackObserved: true, unresolvedCells };
}

export function evaluateBlockedGranularWriteFromRead(
  blockedOperation: ExecutorBlockedOperation,
  readArgs: unknown,
  readOutput: unknown,
  accumulators: ExecutorRunAccumulators
): {
  complete: boolean;
  readbackObserved: boolean;
  unresolvedCells: string[];
  confirmedEditedCells: string[];
} {
  if (
    blockedOperation.toolName !== "set_range_values" &&
    blockedOperation.toolName !== "set_range_formulas"
  ) {
    return {
      complete: false,
      readbackObserved: false,
      unresolvedCells: [],
      confirmedEditedCells: [],
    };
  }
  const target = readbackContentTargetsFromWriteArgs(
    blockedOperation.toolName,
    blockedOperation.args
  )[0];
  const readArgsRecord = recordValue(readArgs);
  const readSheetName = stringValue(readArgsRecord.sheetName);
  const readOutputRecord = recordValue(readOutput);
  const outputSheetName = stringValue(readOutputRecord.sheetName);
  if (
    !target ||
    !readSheetName ||
    normalizeSheetName(readSheetName) !== normalizeSheetName(target.sheetName) ||
    (outputSheetName &&
      normalizeSheetName(outputSheetName) !== normalizeSheetName(target.sheetName)) ||
    !hasDenseReadbackEvidence(target, readOutputRecord)
  ) {
    return {
      complete: false,
      readbackObserved: false,
      unresolvedCells: [],
      confirmedEditedCells: [],
    };
  }
  const evaluation = evaluateWorksheetContentReadback(target, readArgs, readOutput);
  const missing = new Set(
    evaluation.missingCells.map((cell) => `${cell.kind}|${normalizeLocalAddressKey(cell.address)}`)
  );
  const confirmedCells = target.cells.filter(
    (cell) => !missing.has(`${cell.kind}|${normalizeLocalAddressKey(cell.address)}`)
  );
  const confirmedEditedCells = recordReadbackConfirmedGranularWrite(
    accumulators,
    blockedOperation.toolName,
    recordValue(blockedOperation.args),
    target,
    confirmedCells
  );
  return {
    complete: evaluation.complete,
    readbackObserved: true,
    unresolvedCells: evaluation.missingCells.map((cell) => cell.address),
    confirmedEditedCells,
  };
}

export function updatePendingShapeCorrections(
  state: ExecutorVerificationState,
  toolName: string,
  parsedArgs: unknown,
  parsedOutput: unknown
): void {
  const output = recordValue(parsedOutput);
  const args = recordValue(parsedArgs);
  const stepId = typeof args.stepId === "string" ? args.stepId : null;
  if (output.code === "range_matrix_shape_mismatch") {
    const targetAddress =
      stringValue(output.targetAddress) ?? stringValue(args.address) ?? "(unknown range)";
    const matchingTarget = readbackContentTargetsFromWriteArgs(toolName, parsedArgs).find(
      (target) =>
        normalizeLocalAddressKey(target.address) === normalizeLocalAddressKey(targetAddress)
    );
    const sheetName = matchingTarget?.sheetName ?? stringValue(args.sheetName);
    const matrixField =
      output.matrixField === "values" || output.matrixField === "formulas"
        ? output.matrixField
        : undefined;
    const existing = state.pendingShapeCorrections.find(
      (correction) =>
        correction.toolName === toolName &&
        correction.stepId === stepId &&
        normalizeSheetName(correction.sheetName) === normalizeSheetName(sheetName) &&
        normalizeLocalAddressKey(correction.address) === normalizeLocalAddressKey(targetAddress) &&
        correction.matrixField === matrixField
    );
    if (!existing) {
      state.pendingShapeCorrections.push({
        toolName,
        stepId,
        ...(sheetName ? { sheetName } : {}),
        address: targetAddress,
        ...(matrixField ? { matrixField } : {}),
        attempted: false,
      });
    }
    return;
  }
  if (output.applied === true) {
    const resolved = findMatchingShapeCorrection(
      state.pendingShapeCorrections,
      toolName,
      stepId,
      parsedArgs
    );
    if (resolved) {
      state.pendingShapeCorrections = state.pendingShapeCorrections.filter(
        (correction) => correction !== resolved
      );
    }
  }
}

export function readbackContentTargetsFromWriteCompletion(
  toolName: string,
  parsedArgs: unknown,
  parsedOutput: unknown
): ExecutorContentReadbackTarget[] {
  if (
    toolName !== "set_range_values" &&
    toolName !== "set_range_formulas" &&
    toolName !== "modify_workbook"
  ) {
    return [];
  }
  const output = recordValue(parsedOutput);
  const editedCells = Array.isArray(output.editedCells)
    ? output.editedCells.filter(
        (address): address is string => typeof address === "string" && address.trim().length > 0
      )
    : [];
  if (output.applied !== true && editedCells.length === 0) return [];
  const targets = readbackContentTargetsFromWriteArgs(toolName, parsedArgs);
  if (output.applied === true) return targets;
  const partialTargets: ExecutorContentReadbackTarget[] = [];
  const rangedTargets = targets
    .map((target) => ({ target, range: parseA1Range(target.address) }))
    .filter(
      (
        entry
      ): entry is {
        target: ExecutorContentReadbackTarget;
        range: ParsedA1Range;
      } => !!entry.range
    );
  for (const editedCell of editedCells) {
    const editedRange = parseA1Range(editedCell);
    if (!editedRange) continue;
    const matches = rangedTargets.filter(
      ({ range }) =>
        range.startRow <= editedRange.endRow &&
        range.endRow >= editedRange.startRow &&
        range.startColumn <= editedRange.endColumn &&
        range.endColumn >= editedRange.startColumn
    );
    // Host evidence is currently local-address only. When the same local range
    // occurs in more than one requested target (for example SheetA!A1 and
    // SheetB!A1), assigning it to either target would be a false confirmation.
    if (matches.length !== 1) continue;
    const { target, range: targetRange } = matches[0];
    const startRow = Math.max(targetRange.startRow, editedRange.startRow);
    const endRow = Math.min(targetRange.endRow, editedRange.endRow);
    const startColumn = Math.max(targetRange.startColumn, editedRange.startColumn);
    const endColumn = Math.min(targetRange.endColumn, editedRange.endColumn);
    if (startRow > endRow || startColumn > endColumn) continue;
    const cells = target.cells.filter((cell) => {
      const position = parseA1Cell(cell.address);
      return (
        !!position &&
        position.row >= startRow &&
        position.row <= endRow &&
        position.column >= startColumn &&
        position.column <= endColumn
      );
    });
    if (cells.length === 0) continue;
    const startAddress = formatA1Cell(startRow, startColumn);
    const endAddress = formatA1Cell(endRow, endColumn);
    partialTargets.push({
      sheetName: target.sheetName,
      address: startAddress === endAddress ? startAddress : `${startAddress}:${endAddress}`,
      rowCount: endRow - startRow + 1,
      columnCount: endColumn - startColumn + 1,
      cells,
    });
  }
  return partialTargets;
}

export function readbackContentTargetsFromWriteArgs(
  toolName: string,
  parsedArgs: unknown
): ExecutorContentReadbackTarget[] {
  const args = recordValue(parsedArgs);
  const targets: ExecutorContentReadbackTarget[] = [];
  if (toolName === "set_range_values") {
    addMatrixContentReadbackTarget(targets, args.sheetName, args.address, args.values, "values");
    return targets;
  }
  if (toolName === "set_range_formulas") {
    addMatrixContentReadbackTarget(
      targets,
      args.sheetName,
      args.address,
      args.formulas,
      "formulas"
    );
    return targets;
  }

  const modifications =
    args.modifications !== null && typeof args.modifications === "object"
      ? recordValue(args.modifications)
      : args;
  const worksheets = Array.isArray(modifications.worksheets) ? modifications.worksheets : [];
  for (const worksheetValue of worksheets) {
    const worksheet = recordValue(worksheetValue);
    const sheetName = stringValue(worksheet.sheetName) ?? stringValue(args.targetSheet);
    const cells = Array.isArray(worksheet.cells) ? worksheet.cells : [];
    for (const cellValue of cells) {
      const cell = recordValue(cellValue);
      if (cell.action !== "set") continue;
      if (cell.formula !== undefined) {
        addScalarContentReadbackTarget(
          targets,
          sheetName,
          cell.address,
          cell.formula,
          cell.formula === null ? "blank" : "formula"
        );
      } else if (cell.value !== undefined) {
        addScalarContentReadbackTarget(
          targets,
          sheetName,
          cell.address,
          cell.value,
          cell.value === null
            ? "blank"
            : typeof cell.value === "string" && cell.value.startsWith("=")
              ? "formula"
              : "value"
        );
      }
    }
    const ranges = Array.isArray(worksheet.ranges) ? worksheet.ranges : [];
    for (const rangeValue of ranges) {
      const range = recordValue(rangeValue);
      if (range.action !== "fill") continue;
      if (range.formulas !== undefined) {
        addMatrixContentReadbackTarget(
          targets,
          sheetName,
          range.address,
          range.formulas,
          "formulas"
        );
      } else if (range.values !== undefined) {
        addMatrixContentReadbackTarget(targets, sheetName, range.address, range.values, "values");
      }
    }
  }
  return targets;
}

export function addScalarContentReadbackTarget(
  targets: ExecutorContentReadbackTarget[],
  sheetNameValue: unknown,
  addressValue: unknown,
  value: unknown,
  kind: "formula" | "value" | "blank"
): void {
  const sheetName = normalizeSheetName(sheetNameValue);
  const address = stringValue(addressValue);
  const range = address ? parseA1Range(address) : undefined;
  if (
    !sheetName ||
    !address ||
    !range ||
    value === undefined ||
    (kind === "blank" && value !== null) ||
    (kind === "formula" && typeof value !== "string") ||
    (kind === "value" &&
      typeof value !== "string" &&
      typeof value !== "number" &&
      typeof value !== "boolean")
  ) {
    return;
  }
  const cells: ExecutorExpectedContentCell[] = [];
  for (let row = range.startRow; row <= range.endRow; row += 1) {
    for (let column = range.startColumn; column <= range.endColumn; column += 1) {
      cells.push({
        address: formatA1Cell(row, column),
        kind,
        expected: value,
      } as ExecutorExpectedContentCell);
    }
  }
  targets.push({
    sheetName,
    address,
    rowCount: range.endRow - range.startRow + 1,
    columnCount: range.endColumn - range.startColumn + 1,
    cells,
  });
}

export function addMatrixContentReadbackTarget(
  targets: ExecutorContentReadbackTarget[],
  sheetNameValue: unknown,
  addressValue: unknown,
  matrixValue: unknown,
  field: "values" | "formulas"
): void {
  const sheetName = normalizeSheetName(sheetNameValue);
  const address = stringValue(addressValue);
  const range = address ? parseA1Range(address) : undefined;
  if (!sheetName || !address || !range || !Array.isArray(matrixValue)) return;
  const cells: ExecutorExpectedContentCell[] = [];
  for (let rowOffset = 0; rowOffset < matrixValue.length; rowOffset += 1) {
    const row = matrixValue[rowOffset];
    if (!Array.isArray(row)) continue;
    for (let columnOffset = 0; columnOffset < row.length; columnOffset += 1) {
      const value = row[columnOffset];
      if (
        value === undefined ||
        (value !== null &&
          typeof value !== "string" &&
          typeof value !== "number" &&
          typeof value !== "boolean")
      ) {
        continue;
      }
      const kind =
        value === null
          ? "blank"
          : field === "formulas" || (typeof value === "string" && value.startsWith("="))
            ? "formula"
            : "value";
      if (kind === "formula" && typeof value !== "string") continue;
      cells.push({
        address: formatA1Cell(range.startRow + rowOffset, range.startColumn + columnOffset),
        kind,
        expected: value,
      });
    }
  }
  if (cells.length === 0) return;
  targets.push({
    sheetName,
    address,
    rowCount: range.endRow - range.startRow + 1,
    columnCount: range.endColumn - range.startColumn + 1,
    cells,
  });
}

/** Drop every outstanding content expectation and the failures they produced. */
export function clearContentReadbackVerification(state: ExecutorVerificationState): void {
  state.pendingContentReadbackTargets = [];
  state.verifiedContentReadbackTargetKeys = [];
  state.contentReadbackFailures = [];
  const hasDurableVerificationDebt =
    state.refreshRequired ||
    state.recalcRequired ||
    state.hardErrors.length > 0 ||
    state.pendingShapeCorrections.length > 0 ||
    !allPendingReadbackSheetsVerified(state) ||
    !allPendingReadbackTargetsVerified(state) ||
    ((state.refreshObserved || state.recalcObserved) &&
      !state.cleanReadbackAfterRecalc);
  if (!hasDurableVerificationDebt) {
    state.requiresVerification = false;
  }
}

/**
 * Decide whether a write needs its own confirming read-back.
 *
 * A cleanly applied static write is already acknowledged by the host, so
 * demanding a full-shape read of every such write costs a host round trip and
 * an extra model iteration for no new evidence. Reserve the read for writes
 * whose outcome is genuinely uncertain: an ambiguous or partial host result, or
 * FDS-bound formulas, which only produce their real content after the trailing
 * refresh and recalculate pass.
 */
export function requiresContentReadbackVerification(
  toolName: string,
  parsedArgs: unknown,
  parsedOutput: unknown
): boolean {
  const output = recordValue(parsedOutput);
  const hostResultUncertain =
    output.applied !== true ||
    output.ambiguousWrite === true ||
    output.retryReason !== undefined ||
    (Array.isArray(output.failedActions) && output.failedActions.length > 0) ||
    (Array.isArray(output.skippedActions) && output.skippedActions.length > 0);
  if (hostResultUncertain) return true;
  return readbackContentTargetsFromWriteArgs(toolName, parsedArgs).some((target) =>
    target.cells.some((cell) => cell.kind === "formula" && isFdsBoundFormula(cell.expected))
  );
}

/**
 * Retire expectations that a newer confirmed write has overwritten.
 *
 * Pending targets are keyed by `sheet!address`, so a later narrower write does
 * not replace a wider earlier target. Without this, filling `B2:K31` and then
 * writing formulas into `E6:K30` leaves the `B2:K31` target expecting the
 * original content at 175 coordinates that no longer hold it — an expectation
 * no read-back can ever satisfy, which strands the run in "needs attention"
 * and makes every continuation replay the same dead end.
 */
export function supersedeContentReadbackCells(
  state: ExecutorVerificationState,
  toolName: string,
  parsedArgs: unknown,
  parsedOutput: unknown,
  confirmedTargets: readonly ExecutorContentReadbackTarget[]
): void {
  if (state.pendingContentReadbackTargets.length === 0) return;
  const supersededBySheet = new Map<string, Set<string>>();
  const addSuperseded = (sheetName: string, address: string): void => {
    const sheetKey = normalizeSheetName(sheetName) ?? sheetName;
    const addresses = supersededBySheet.get(sheetKey) ?? new Set<string>();
    addresses.add(normalizeLocalAddressKey(address));
    supersededBySheet.set(sheetKey, addresses);
  };
  for (const target of confirmedTargets) {
    for (const cell of target.cells) addSuperseded(target.sheetName, cell.address);
  }
  for (const cleared of clearedRangesFromWriteCompletion(toolName, parsedArgs, parsedOutput)) {
    const range = parseA1Range(cleared.address);
    if (!range) continue;
    for (let row = range.startRow; row <= range.endRow; row += 1) {
      for (let column = range.startColumn; column <= range.endColumn; column += 1) {
        addSuperseded(cleared.sheetName, formatA1Cell(row, column));
      }
    }
  }
  if (supersededBySheet.size === 0) return;

  // Targets the incoming write replaces outright are handled by the merge that
  // follows; only older, differently-addressed targets need trimming here.
  const incomingKeys = new Set(confirmedTargets.map(contentReadbackTargetKey));
  const trimmedKeys = new Set<string>();
  const survivors: ExecutorContentReadbackTarget[] = [];
  for (const target of state.pendingContentReadbackTargets) {
    const key = contentReadbackTargetKey(target);
    const superseded = supersededBySheet.get(
      normalizeSheetName(target.sheetName) ?? target.sheetName
    );
    if (incomingKeys.has(key) || !superseded) {
      survivors.push(target);
      continue;
    }
    const cells = target.cells.filter(
      (cell) => !superseded.has(normalizeLocalAddressKey(cell.address))
    );
    if (cells.length === target.cells.length) {
      survivors.push(target);
      continue;
    }
    trimmedKeys.add(key);
    // The address stays as written so the shape check keeps asking for the
    // same rectangle; only the per-coordinate expectations shrink.
    if (cells.length > 0) survivors.push({ ...target, cells });
  }
  if (trimmedKeys.size === 0) return;
  state.pendingContentReadbackTargets = survivors;
  const survivingKeys = new Set(survivors.map(contentReadbackTargetKey));
  state.verifiedContentReadbackTargetKeys = state.verifiedContentReadbackTargetKeys.filter((key) =>
    survivingKeys.has(key)
  );
  // A recorded failure names coordinates that may no longer be expected, so
  // drop it and let the next read-back produce a current reason.
  state.contentReadbackFailures = state.contentReadbackFailures.filter(
    (failure) => survivingKeys.has(failure.key) && !trimmedKeys.has(failure.key)
  );
}

/** Confirmed `clear` range actions, which also retire earlier expectations. */
export function clearedRangesFromWriteCompletion(
  toolName: string,
  parsedArgs: unknown,
  parsedOutput: unknown
): Array<{ sheetName: string; address: string }> {
  if (recordValue(parsedOutput).applied !== true) return [];
  const args = recordValue(parsedArgs);
  if (toolName === "clear_range") {
    const clearType = stringValue(args.clearType);
    const sheetName = stringValue(args.sheetName);
    const address = stringValue(args.address);
    return (clearType === "Contents" || clearType === "All") && sheetName && address
      ? [{ sheetName, address }]
      : [];
  }
  if (toolName !== "modify_workbook") return [];
  const modifications =
    args.modifications !== null && typeof args.modifications === "object"
      ? recordValue(args.modifications)
      : args;
  const worksheets = Array.isArray(modifications.worksheets) ? modifications.worksheets : [];
  const cleared: Array<{ sheetName: string; address: string }> = [];
  for (const worksheetValue of worksheets) {
    const worksheet = recordValue(worksheetValue);
    const sheetName = stringValue(worksheet.sheetName) ?? stringValue(args.targetSheet);
    if (!sheetName) continue;
    for (const rangeValue of Array.isArray(worksheet.ranges) ? worksheet.ranges : []) {
      const range = recordValue(rangeValue);
      const address = stringValue(range.address);
      const clearType = stringValue(recordValue(range.clear).clearType) ?? "All";
      if (
        range.action === "clear" &&
        (clearType === "Contents" || clearType === "All") &&
        address
      ) {
        cleared.push({ sheetName, address });
      }
    }
  }
  return cleared;
}

export function mergePendingContentReadbackTargets(
  state: ExecutorVerificationState,
  targets: readonly ExecutorContentReadbackTarget[]
): void {
  const merged = new Map(
    state.pendingContentReadbackTargets.map((target) => [contentReadbackTargetKey(target), target])
  );
  const replacedKeys = new Set<string>();
  for (const target of targets) {
    const key = contentReadbackTargetKey(target);
    merged.set(key, target);
    replacedKeys.add(key);
  }
  state.pendingContentReadbackTargets = Array.from(merged.values());
  state.verifiedContentReadbackTargetKeys = state.verifiedContentReadbackTargetKeys.filter(
    (key) => !replacedKeys.has(key) && merged.has(key)
  );
  state.contentReadbackFailures = state.contentReadbackFailures.filter(
    (failure) => !replacedKeys.has(failure.key) && merged.has(failure.key)
  );
}

export function updateContentReadbackVerification(
  state: ExecutorVerificationState,
  parsedArgs: unknown,
  parsedOutput: unknown
): void {
  if (state.pendingContentReadbackTargets.length === 0) return;
  const args = recordValue(parsedArgs);
  const readSheet = normalizeSheetName(
    stringValue(args.sheetName) ?? stringValue(recordValue(parsedOutput).sheetName)
  );
  const requestedAddress = stringValue(args.address);
  const requestedRanges = requestedAddress ? parseA1Ranges(requestedAddress) : [];
  if (!readSheet) return;

  const verified = new Set(state.verifiedContentReadbackTargetKeys);
  const failureByKey = new Map(
    state.contentReadbackFailures.map((failure) => [failure.key, failure.reason])
  );
  for (const target of state.pendingContentReadbackTargets) {
    if (target.sheetName !== readSheet) continue;
    const targetRange = parseA1Range(target.address);
    if (
      !targetRange ||
      (requestedRanges.length > 0 &&
        !requestedRanges.some((range) => rangesOverlap(range, targetRange)))
    ) {
      continue;
    }
    const evaluation = evaluateWorksheetContentReadback(target, parsedArgs, parsedOutput);
    const key = contentReadbackTargetKey(target);
    if (evaluation.complete) {
      verified.add(key);
      failureByKey.delete(key);
    } else {
      verified.delete(key);
      failureByKey.set(
        key,
        evaluation.reason ?? `${target.sheetName}!${target.address} did not match`
      );
    }
  }
  state.verifiedContentReadbackTargetKeys = Array.from(verified);
  state.contentReadbackFailures = Array.from(failureByKey.entries()).map(([key, reason]) => ({
    key,
    reason,
  }));
  if (!allPendingContentReadbackTargetsVerified(state)) {
    state.requiresVerification = true;
  }
}

export function allPendingContentReadbackTargetsVerified(state: ExecutorVerificationState): boolean {
  if (state.pendingContentReadbackTargets.length === 0) return true;
  const verified = new Set(state.verifiedContentReadbackTargetKeys);
  return state.pendingContentReadbackTargets.every((target) =>
    verified.has(contentReadbackTargetKey(target))
  );
}

export function unverifiedContentReadbackTargets(
  state: ExecutorVerificationState
): ExecutorContentReadbackTarget[] {
  const verified = new Set(state.verifiedContentReadbackTargetKeys);
  return state.pendingContentReadbackTargets.filter(
    (target) => !verified.has(contentReadbackTargetKey(target))
  );
}

export function contentReadbackTargetKey(target: ExecutorContentReadbackTarget): string {
  return `${target.sheetName}!${normalizeLocalAddressKey(target.address)}`;
}

export function normalizeLocalAddressKey(address: string): string {
  const local = address.includes("!") ? address.slice(address.lastIndexOf("!") + 1) : address;
  return local.replace(/\$/g, "").trim().toUpperCase();
}

export function formatA1Cell(row: number, column: number): string {
  let remaining = column + 1;
  let letters = "";
  while (remaining > 0) {
    const remainder = (remaining - 1) % 26;
    letters = String.fromCharCode(65 + remainder) + letters;
    remaining = Math.floor((remaining - 1) / 26);
  }
  return `${letters}${row + 1}`;
}

export function mergePendingReadbackSheets(
  state: ExecutorVerificationState,
  sheetNames: readonly string[]
): void {
  const merged = new Set(state.pendingReadbackSheetNames);
  for (const sheetName of sheetNames) {
    const normalized = normalizeSheetName(sheetName);
    if (normalized) merged.add(normalized);
  }
  state.pendingReadbackSheetNames = Array.from(merged);
  state.verifiedReadbackSheetNames = state.verifiedReadbackSheetNames.filter((sheetName) =>
    merged.has(sheetName)
  );
}

export function mergePendingReadbackTargets(
  state: ExecutorVerificationState,
  targets: readonly ExecutorReadbackTarget[]
): void {
  const merged = new Map<string, ExecutorReadbackTarget>();
  for (const target of state.pendingReadbackTargets) {
    merged.set(readbackTargetKey(target), target);
  }
  for (const target of targets) {
    const normalized = normalizeReadbackTarget(target);
    if (normalized) merged.set(readbackTargetKey(normalized), normalized);
  }
  state.pendingReadbackTargets = Array.from(merged.values());
  const pendingKeys = new Set(state.pendingReadbackTargets.map(readbackTargetKey));
  state.verifiedReadbackTargets = state.verifiedReadbackTargets.filter((target) =>
    pendingKeys.has(readbackTargetKey(target))
  );
}

export function markReadbackSheetVerified(
  state: ExecutorVerificationState,
  sheetName: string | undefined
): boolean {
  if (state.pendingReadbackSheetNames.length === 0) return state.requiresVerification;
  const normalized = normalizeSheetName(sheetName);
  if (!normalized || !state.pendingReadbackSheetNames.includes(normalized)) return false;
  if (!state.verifiedReadbackSheetNames.includes(normalized)) {
    state.verifiedReadbackSheetNames.push(normalized);
  }
  return true;
}

export function markReadbackTargetVerified(
  state: ExecutorVerificationState,
  sheetName: string | undefined,
  readAddress: string | undefined
): boolean {
  if (state.pendingReadbackTargets.length === 0) return state.requiresVerification;
  const normalizedSheetName = normalizeSheetName(sheetName);
  if (!normalizedSheetName || !readAddress) return false;
  const readRanges = parseA1Ranges(readAddress);
  if (readRanges.length === 0) return false;
  const matchedTargets = state.pendingReadbackTargets.filter((target) => {
    if (target.sheetName !== normalizedSheetName) return false;
    const targetRange = parseA1Range(target.address);
    return Boolean(
      targetRange && readRanges.some((readRange) => rangeContains(readRange, targetRange))
    );
  });
  if (matchedTargets.length === 0) return false;

  const verified = new Map<string, ExecutorReadbackTarget>();
  for (const target of state.verifiedReadbackTargets) {
    verified.set(readbackTargetKey(target), target);
  }
  for (const target of matchedTargets) {
    verified.set(readbackTargetKey(target), target);
  }
  state.verifiedReadbackTargets = Array.from(verified.values());
  return true;
}

export function isReadbackSheetRelevant(
  state: ExecutorVerificationState,
  sheetName: string | undefined
): boolean {
  if (!state.requiresVerification) return false;
  if (state.pendingReadbackSheetNames.length === 0) return true;
  const normalized = normalizeSheetName(sheetName);
  return Boolean(normalized && state.pendingReadbackSheetNames.includes(normalized));
}

export function allPendingReadbackSheetsVerified(state: ExecutorVerificationState): boolean {
  return state.pendingReadbackSheetNames.every((sheetName) =>
    state.verifiedReadbackSheetNames.includes(sheetName)
  );
}

export function allPendingReadbackTargetsVerified(state: ExecutorVerificationState): boolean {
  if (state.pendingReadbackTargets.length === 0) return true;
  const verified = new Set(state.verifiedReadbackTargets.map(readbackTargetKey));
  return state.pendingReadbackTargets.every((target) => verified.has(readbackTargetKey(target)));
}

export function sheetNameFromReadRange(parsedArgs: unknown, parsedOutput: unknown): string | undefined {
  const argsSheet =
    parsedArgs && typeof parsedArgs === "object"
      ? (parsedArgs as { sheetName?: unknown }).sheetName
      : undefined;
  if (typeof argsSheet === "string" && argsSheet.trim()) return argsSheet;
  const outputSheet =
    parsedOutput && typeof parsedOutput === "object"
      ? (parsedOutput as { sheetName?: unknown }).sheetName
      : undefined;
  return typeof outputSheet === "string" && outputSheet.trim() ? outputSheet : undefined;
}

export function readRangeAddress(parsedArgs: unknown, parsedOutput: unknown): string | undefined {
  const outputAddress =
    parsedOutput && typeof parsedOutput === "object"
      ? (parsedOutput as { address?: unknown }).address
      : undefined;
  if (typeof outputAddress === "string" && outputAddress.trim()) return outputAddress;
  const argsAddress =
    parsedArgs && typeof parsedArgs === "object"
      ? (parsedArgs as { address?: unknown }).address
      : undefined;
  return typeof argsAddress === "string" && argsAddress.trim() ? argsAddress : undefined;
}

export function readRangeReturnedData(parsedOutput: unknown): boolean {
  const output =
    parsedOutput && typeof parsedOutput === "object"
      ? (parsedOutput as { values?: unknown; formulas?: unknown; csv?: unknown })
      : {};
  if (Array.isArray(output.values)) {
    if (
      output.values.some(
        (row) =>
          Array.isArray(row) &&
          row.some(
            (cell) =>
              cell !== null &&
              cell !== undefined &&
              (typeof cell !== "string" || cell.trim() !== "")
          )
      )
    ) {
      return true;
    }
    return (
      Array.isArray(output.formulas) &&
      output.formulas.some(
        (row) => Array.isArray(row) && row.some((formula) => isFdsBoundFormula(formula))
      )
    );
  }
  return typeof output.csv === "string" && output.csv.trim().length > 0;
}

export function classifyRelevantReadbackHardErrors(
  state: ExecutorVerificationState,
  sheetName: string | undefined,
  readAddress: string | undefined,
  parsedOutput: unknown
): ExcelHardErrorCell[] {
  const output =
    parsedOutput && typeof parsedOutput === "object"
      ? (parsedOutput as {
          values?: unknown;
          formulas?: unknown;
          sheetName?: unknown;
          address?: unknown;
          startRow?: unknown;
          startColumn?: unknown;
        })
      : {};

  const formulaErrors =
    Array.isArray(output.values) && Array.isArray(output.formulas)
      ? classifyHardErrorsInFormulaValues(output.values, output.formulas, {
          sheetName: sheetNameFromReadRange({ sheetName }, parsedOutput),
          address: readAddress ?? readRangeAddress({}, parsedOutput),
          startRow: numberValue(output.startRow),
          startColumn: numberValue(output.startColumn),
        }).errors
      : [];

  const pendingTargetErrors = classifyHardErrorsInReadRangeResult({
    ...output,
    sheetName: sheetNameFromReadRange({ sheetName }, parsedOutput),
    address: readAddress ?? readRangeAddress({}, parsedOutput),
  }).errors.filter((error) => isHardErrorInPendingReadbackTarget(state, sheetName, error));

  return mergeHardErrors(formulaErrors, pendingTargetErrors);
}

export function recordUnavailableFdsTargetsAfterCorrection(
  state: ExecutorVerificationState,
  accumulators: ExecutorRunAccumulators,
  errors: readonly ExcelHardErrorCell[]
): void {
  if (!state.refreshObserved || !state.recalcObserved) return;
  const attempts = accumulators.fdsFormulaWriteAttempts ?? {};
  const protectedTargets = new Set(
    (accumulators.fdsFormulaWriteTargets ?? []).map(
      (target) =>
        `${target.sheetName.trim().toLowerCase()}!${normalizeLocalAddressKey(target.address)}`
    )
  );
  const unavailable = (accumulators.unavailableFdsFormulaTargets ??= []);
  const unavailableKeys = new Set(
    unavailable.map(
      (target) =>
        `${target.sheetName.trim().toLowerCase()}!${normalizeLocalAddressKey(target.address)}`
    )
  );
  for (const error of errors) {
    const sheetName = error.sheetName.trim();
    const address = normalizeLocalAddressKey(error.address);
    const key = `${sheetName.toLowerCase()}!${address}`;
    if (
      !sheetName ||
      !address ||
      !protectedTargets.has(key) ||
      (attempts[key] ?? 0) < 2 ||
      unavailableKeys.has(key)
    ) {
      continue;
    }
    unavailableKeys.add(key);
    unavailable.push({ sheetName, address });
  }
}

export function isHardErrorInPendingReadbackTarget(
  state: ExecutorVerificationState,
  sheetName: string | undefined,
  error: ExcelHardErrorCell
): boolean {
  if (state.pendingReadbackTargets.length === 0) return false;
  const normalizedSheetName = normalizeSheetName(sheetName) ?? normalizeSheetName(error.sheetName);
  const errorTarget = readbackTargetFromQualifiedAddress(error.address) ?? {
    sheetName: normalizeSheetName(error.sheetName) ?? "",
    address: error.address,
  };
  if (!normalizedSheetName || errorTarget.sheetName !== normalizedSheetName) return false;
  const errorRange = parseA1Range(errorTarget.address);
  if (!errorRange) return false;
  return state.pendingReadbackTargets.some((target) => {
    if (target.sheetName !== normalizedSheetName) return false;
    const targetRange = parseA1Range(target.address);
    return Boolean(targetRange && rangesOverlap(errorRange, targetRange));
  });
}

export function hardErrorsNotCoveredByCleanRead(
  hardErrors: readonly ExcelHardErrorCell[],
  sheetName: string | undefined,
  readAddress: string | undefined
): ExcelHardErrorCell[] {
  if (hardErrors.length === 0) return [];
  const normalizedSheetName = normalizeSheetName(sheetName);
  const readRanges = readAddress ? parseA1Ranges(readAddress) : [];
  if (!normalizedSheetName || readRanges.length === 0) return Array.from(hardErrors);

  return hardErrors.filter((error) => {
    const target = readbackTargetFromQualifiedAddress(error.address) ?? {
      sheetName: normalizeSheetName(error.sheetName) ?? "",
      address: error.address,
    };
    if (target.sheetName !== normalizedSheetName) return true;
    const errorRange = parseA1Range(target.address);
    return !(errorRange && readRanges.some((readRange) => rangesOverlap(errorRange, readRange)));
  });
}

export function mergeHardErrors(
  existingErrors: readonly ExcelHardErrorCell[],
  newErrors: readonly ExcelHardErrorCell[]
): ExcelHardErrorCell[] {
  const merged = new Map<string, ExcelHardErrorCell>();
  for (const error of existingErrors) {
    merged.set(hardErrorKey(error), error);
  }
  for (const error of newErrors) {
    merged.set(hardErrorKey(error), error);
  }
  return Array.from(merged.values());
}

export function hardErrorKey(error: ExcelHardErrorCell): string {
  return `${normalizeSheetName(error.sheetName) ?? ""}!${error.address.toUpperCase()}!${error.token}`;
}

export function readbackSheetsFromRefreshResult(
  parsedArgs: unknown,
  parsedOutput: unknown,
  accumulators: ExecutorRunAccumulators,
  state: ExecutorVerificationState
): string[] {
  const result =
    parsedOutput && typeof parsedOutput === "object"
      ? (parsedOutput as {
          scopeConfirmed?: unknown;
          refreshedSheetNames?: unknown;
        })
      : {};
  if (result.scopeConfirmed === false) return [];
  if (Array.isArray(result.refreshedSheetNames)) {
    const confirmedSheets = result.refreshedSheetNames.filter(
      (sheetName): sheetName is string =>
        typeof sheetName === "string" && sheetName.trim().length > 0
    );
    const confirmedKeys = new Set(
      confirmedSheets.map((sheetName) => sheetName.trim().toLowerCase())
    );
    const pendingConfirmed = state.pendingReadbackSheetNames.filter((sheetName) =>
      confirmedKeys.has(sheetName.trim().toLowerCase())
    );
    if (pendingConfirmed.length > 0) return pendingConfirmed;
    const requestedSheets = recordValue(parsedArgs).sheetNames;
    if (Array.isArray(requestedSheets)) {
      return requestedSheets.filter(
        (sheetName): sheetName is string =>
          typeof sheetName === "string" && confirmedKeys.has(sheetName.trim().toLowerCase())
      );
    }
    return [];
  }
  const args =
    parsedArgs && typeof parsedArgs === "object"
      ? (parsedArgs as {
          scope?: unknown;
          sheetNames?: unknown;
          refreshAll?: unknown;
        })
      : {};
  if (Array.isArray(args.sheetNames)) {
    const sheets = args.sheetNames.filter(
      (sheetName): sheetName is string =>
        typeof sheetName === "string" && sheetName.trim().length > 0
    );
    if (sheets.length > 0) return sheets;
  }
  if (args.scope === "workbook" || args.refreshAll === true) {
    const fdsSheets = Array.from(accumulators.fdsRefreshSheets);
    return fdsSheets.length > 0 ? fdsSheets : state.pendingReadbackSheetNames;
  }
  return [];
}

export function readbackSheetsFromWriteArgs(
  parsedArgs: unknown,
  accumulators: ExecutorRunAccumulators
): string[] {
  const sheets = new Set<string>();
  const args =
    parsedArgs && typeof parsedArgs === "object" ? (parsedArgs as Record<string, unknown>) : {};
  addReadbackSheet(sheets, args.sheetName);
  addReadbackSheet(sheets, args.targetSheet);

  const modifications =
    args.modifications && typeof args.modifications === "object"
      ? (args.modifications as { worksheets?: unknown })
      : args;
  const worksheets = Array.isArray(modifications.worksheets) ? modifications.worksheets : [];
  for (const worksheet of worksheets) {
    if (worksheet && typeof worksheet === "object") {
      addReadbackSheet(sheets, (worksheet as { sheetName?: unknown }).sheetName);
    }
  }

  for (const editedCell of accumulators.editedCells) {
    addReadbackSheet(sheets, sheetNameFromQualifiedAddress(editedCell));
  }

  return Array.from(sheets);
}

export function readbackFormulaTargetsFromWriteArgs(parsedArgs: unknown): ExecutorReadbackTarget[] {
  const targets: ExecutorReadbackTarget[] = [];
  const args =
    parsedArgs && typeof parsedArgs === "object" ? (parsedArgs as Record<string, unknown>) : {};
  const topLevelSheetName = stringValue(args.sheetName) || stringValue(args.targetSheet);
  if (isFdsBoundFormula(args.formula) || formulaMatrixHasFdsBoundFormula(args.formulas)) {
    addReadbackTarget(targets, topLevelSheetName, args.address);
  }

  const modifications =
    args.modifications && typeof args.modifications === "object"
      ? (args.modifications as { worksheets?: unknown })
      : args;
  const worksheets = Array.isArray(modifications.worksheets) ? modifications.worksheets : [];
  for (const worksheet of worksheets) {
    if (!worksheet || typeof worksheet !== "object") continue;
    const sheetName = stringValue((worksheet as { sheetName?: unknown }).sheetName);
    const cells = (worksheet as { cells?: unknown }).cells;
    if (Array.isArray(cells)) {
      for (const cell of cells) {
        if (cell && typeof cell === "object") {
          const cellRecord = cell as { action?: unknown; address?: unknown; formula?: unknown };
          if (cellRecord.action === "set" && isFdsBoundFormula(cellRecord.formula)) {
            addReadbackTarget(targets, sheetName, cellRecord.address);
          }
        }
      }
    }
    const ranges = (worksheet as { ranges?: unknown }).ranges;
    if (Array.isArray(ranges)) {
      for (const range of ranges) {
        if (range && typeof range === "object") {
          const rangeRecord = range as {
            action?: unknown;
            address?: unknown;
            formulas?: unknown;
          };
          if (
            rangeRecord.action === "fill" &&
            formulaMatrixHasFdsBoundFormula(rangeRecord.formulas)
          ) {
            addReadbackTarget(targets, sheetName, rangeRecord.address);
          }
        }
      }
    }
  }

  return targets;
}

export function addReadbackSheet(sheets: Set<string>, value: unknown): void {
  const normalized = normalizeSheetName(value);
  if (normalized) sheets.add(normalized);
}

export function addReadbackTarget(
  targets: ExecutorReadbackTarget[],
  sheetName: unknown,
  address: unknown
): void {
  const normalizedSheetName = normalizeSheetName(sheetName);
  if (normalizedSheetName && typeof address === "string" && address.trim()) {
    targets.push({ sheetName: normalizedSheetName, address: address.trim() });
  }
}

export function formulaMatrixHasFdsBoundFormula(formulas: unknown): boolean {
  return (
    Array.isArray(formulas) &&
    formulas.some((row) => Array.isArray(row) && row.some((formula) => isFdsBoundFormula(formula)))
  );
}

export function sheetNameFromQualifiedAddress(address: string): string | undefined {
  const bang = address.lastIndexOf("!");
  if (bang <= 0) return undefined;
  return address.slice(0, bang).replace(/^'|'$/g, "");
}

export function readbackTargetFromQualifiedAddress(address: string): ExecutorReadbackTarget | undefined {
  const bang = address.lastIndexOf("!");
  if (bang <= 0) return undefined;
  const sheetName = normalizeSheetName(address.slice(0, bang).replace(/^'|'$/g, ""));
  const localAddress = address.slice(bang + 1).trim();
  return sheetName && localAddress ? { sheetName, address: localAddress } : undefined;
}

export function normalizeSheetName(sheetName: unknown): string | undefined {
  return typeof sheetName === "string" && sheetName.trim()
    ? sheetName.trim().toLowerCase()
    : undefined;
}

export function normalizeReadbackTarget(
  target: ExecutorReadbackTarget
): ExecutorReadbackTarget | undefined {
  const sheetName = normalizeSheetName(target.sheetName);
  const address = typeof target.address === "string" ? target.address.trim() : "";
  return sheetName && address ? { sheetName, address } : undefined;
}

export function readbackTargetKey(target: ExecutorReadbackTarget): string {
  return `${target.sheetName}!${target.address.toUpperCase()}`;
}

export interface ParsedA1Range {
  startRow: number;
  endRow: number;
  startColumn: number;
  endColumn: number;
}

export const EXCEL_MAX_ROW_INDEX = 1_048_575;
export const EXCEL_MAX_COLUMN_INDEX = 16_383;

export function parseA1Range(address: string): ParsedA1Range | undefined {
  const localAddress = address.includes("!")
    ? address.slice(address.lastIndexOf("!") + 1)
    : address;
  const [startRaw, endRaw = startRaw] = localAddress.split(":");
  const start = parseA1Cell(startRaw);
  const end = parseA1Cell(endRaw);
  if (start && end) {
    return {
      startRow: Math.min(start.row, end.row),
      endRow: Math.max(start.row, end.row),
      startColumn: Math.min(start.column, end.column),
      endColumn: Math.max(start.column, end.column),
    };
  }
  const startColumn = parseA1Column(startRaw);
  const endColumn = parseA1Column(endRaw);
  if (startColumn !== undefined && endColumn !== undefined) {
    return {
      startRow: 0,
      endRow: EXCEL_MAX_ROW_INDEX,
      startColumn: Math.min(startColumn, endColumn),
      endColumn: Math.max(startColumn, endColumn),
    };
  }
  const startRow = parseA1Row(startRaw);
  const endRow = parseA1Row(endRaw);
  if (startRow !== undefined && endRow !== undefined) {
    return {
      startRow: Math.min(startRow, endRow),
      endRow: Math.max(startRow, endRow),
      startColumn: 0,
      endColumn: EXCEL_MAX_COLUMN_INDEX,
    };
  }
  return undefined;
}

export function parseA1Ranges(address: string): ParsedA1Range[] {
  return address
    .split(",")
    .map((area) => parseA1Range(area.trim()))
    .filter((area): area is ParsedA1Range => area !== undefined);
}

export function parseA1Cell(address: string | undefined): { row: number; column: number } | undefined {
  const match = /^([A-Z]+)(\d+)$/i.exec(address?.replace(/\$/g, "").trim() ?? "");
  if (!match) return undefined;
  return {
    row: Number(match[2]) - 1,
    column: columnIndex(match[1]),
  };
}

export function parseA1Column(address: string | undefined): number | undefined {
  const match = /^([A-Z]+)$/i.exec(address?.replace(/\$/g, "").trim() ?? "");
  if (!match) return undefined;
  const column = columnIndex(match[1]);
  return column >= 0 && column <= EXCEL_MAX_COLUMN_INDEX ? column : undefined;
}

export function parseA1Row(address: string | undefined): number | undefined {
  const match = /^(\d+)$/.exec(address?.replace(/\$/g, "").trim() ?? "");
  if (!match) return undefined;
  const row = Number(match[1]) - 1;
  return row >= 0 && row <= EXCEL_MAX_ROW_INDEX ? row : undefined;
}

export function rangesOverlap(a: ParsedA1Range, b: ParsedA1Range): boolean {
  return (
    a.startRow <= b.endRow &&
    a.endRow >= b.startRow &&
    a.startColumn <= b.endColumn &&
    a.endColumn >= b.startColumn
  );
}

export function rangeContains(container: ParsedA1Range, target: ParsedA1Range): boolean {
  return (
    container.startRow <= target.startRow &&
    container.endRow >= target.endRow &&
    container.startColumn <= target.startColumn &&
    container.endColumn >= target.endColumn
  );
}
/**
 * Wraps the template outcome callback so the runner can observe
 * `apply_template` side effects without requiring `ExcelApp.tsx` to point its
 * legacy per-step buffer at the multi-hop run.
 *
 * @param context - Existing global tool-call context.
 * @param buffer - Run-scoped destination for template outcomes.
 * @returns A capturing context, or the original context when unavailable.
 */
export function withApplyTemplateOutcomeCapture(
  context: ToolCallContext | undefined,
  buffer: ApplyTemplateOutcome[]
): ToolCallContext | undefined {
  const templateContext = context?.templateContext;
  if (!templateContext) return context;
  return {
    ...context,
    templateContext: {
      ...templateContext,
      onApplyTemplateOutcome: (outcome) => {
        buffer.push(outcome);
        templateContext.onApplyTemplateOutcome?.(outcome);
      },
    },
  };
}
/**
 * Safely parses a JSON string, returning an empty object on failure.
 */
export function safeParseJson(raw: string): unknown {
  try {
    return JSON.parse(raw || "{}");
  } catch {
    return {};
  }
}
/**
 * Throws a DOMException if the signal is already aborted.
 */
export function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new DOMException("Aborted", "AbortError");
  }
}
// ---------------------------------------------------------------------------
// apply_template re-pin
// ---------------------------------------------------------------------------

export type ApplyTemplateProcessResult = { shouldStop: false } | { shouldStop: true; reason: string };

/**
 * Processes buffered `ApplyTemplateOutcome`s after an iteration that called
 * `apply_template`. Handles workbook re-pinning when a template opens a new
 * workbook, and sets `session.skipFinalVerificationReason` when re-pin fails
 * but the template itself succeeded.
 *
 * This is the multi-hop equivalent of `resolveApplyTemplateStep` in the
 * legacy path but lighter: the model already sees the tool response and can
 * self-correct, so the runner only needs to handle host-level re-targeting.
 */
export async function processApplyTemplateOutcomes(
  outcomes: ApplyTemplateOutcome[],
  session: ExecutorRunSessionState,
  opts: {
    probeActiveContext?: () => Promise<{ workbookName?: string }>;
    bridgeAvailable: boolean;
    onWorkbookHandoff?: (
      handoff: ExecutorWorkbookHandoff,
    ) => void | Promise<void>;
  },
  onEvent?: (event: ExecutorRunEvent) => void
): Promise<ApplyTemplateProcessResult> {
  const outcome = outcomes.find((o) => o.success) ?? outcomes[outcomes.length - 1];
  if (!outcome || !outcome.success) return { shouldStop: false };

  const openedAwayFromCurrent =
    outcome.targetWorkbook === "new" || outcome.retargetedFrom === "current";

  if (!openedAwayFromCurrent) return { shouldStop: false };
  const previousPinnedWorkbookName = session.pinnedWorkbookName;

  if (!opts.bridgeAvailable || !opts.probeActiveContext) {
    const reason =
      "Template opened in a new workbook but bridge unavailable for re-pin - skipping verification.";
    session.skipFinalVerificationReason = reason;
    onEvent?.({
      type: "status",
      message: "apply_template opened new workbook; bridge unavailable for re-pin.",
    });
    return { shouldStop: true, reason };
  }

  let newWorkbookName = "";
  try {
    const ctx = await opts.probeActiveContext();
    newWorkbookName = ctx.workbookName?.trim() ?? "";
  } catch (e) {
    console.warn("[Excel] Could not identify workbook opened by apply_template:", e);
  }

  if (newWorkbookName) {
    session.pinnedWorkbookName = newWorkbookName;
    try {
      await opts.onWorkbookHandoff?.({
        sourceWorkbookName: previousPinnedWorkbookName,
        targetWorkbookName: newWorkbookName,
        reason: "apply_template",
      });
    } catch (error) {
      const reason =
        `Template opened in "${newWorkbookName}", but moving the conversation to that workbook failed: ` +
        `${error instanceof Error ? error.message : String(error)}`;
      session.skipFinalVerificationReason = reason;
      onEvent?.({
        type: "status",
        message: `apply_template conversation handoff to "${newWorkbookName}" failed.`,
      });
      return { shouldStop: true, reason };
    }
    onEvent?.({ type: "status", message: `Re-pinned to template workbook "${newWorkbookName}".` });
    diag("llm", "multi_hop_executor_repin", { newWorkbookName });
    return { shouldStop: false };
  } else {
    const reason =
      "Template opened in a new workbook but couldn't identify it - skipping verification against the old workbook.";
    session.skipFinalVerificationReason = reason;
    onEvent?.({
      type: "status",
      message: "apply_template re-pin failed - could not identify new workbook.",
    });
    return { shouldStop: true, reason };
  }
}
